import {
  Zap,
  Clock,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Activity,
  ChevronDown,
  ChevronRight,
  Circle,
  Code2,
  ExternalLink,
  Eye,
  ListChecks,
  MousePointer2,
  Network,
  Rocket,
  BookOpen,
  ShieldCheck,
  PencilLine,
  type LucideIcon,
} from 'lucide-react'
import { RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useStudioLocale } from '@/i18n'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'
import { useMessageListAutoscroll } from '@/hooks/use-message-list-autoscroll'
import {
  getStudioExecutionCopy,
  resolveStudioConfirmationFailureCopy,
  type StudioExecutionCopy,
} from '@/i18n/studio-execution-copy'
import {
  getChatMessagesCopy,
  type ChatCapabilityCardStatus,
  type ChatMessagesCopy,
} from './chat-messages-copy'
import { useStudioStore } from '../../../store/studio-store'
import {
  COOGO_IDE_CHROME_COPY,
  worklogCardTitle,
} from '../coogo-ide-chrome-copy'
import type { CoogoIdeChromeCopy } from '../coogo-ide-chrome-copy'
import { ChatPageProgress } from './chat-page-progress'
import { CoogoStatusMark } from '@/components/coogo-status-mark'
import {
  AMBIGUOUS_TARGET_REASON,
  LOCAL_EDIT_PLAN_UNAVAILABLE_REASON,
  humanizeAppGenerationFailureReason,
  humanizeChatRequestBlocker,
  humanizeFailureReason,
} from './miss-reason-copy'
import { submitIntentChunkPlan } from '@/features/chat/intent-chunks'
import { INK_500, INK_700, PURPLE, FONT_MONO, OK, DANGER, MUTED_TEXT, SUBTLE_BG } from './chat-tokens'
import type {
  ChatMessage,
  WorklogEntryCode,
  WorklogCardId,
} from '@/features/channel-router'
import { useChannelDispatch } from '@/features/channel-router'
import type { CollaborationRole } from '@/features/channel-router'
import { collaborationTone, entryRoleFromTone, inferWorklogRole } from '@/features/chat/lib/collaboration-palette'
import type {
  ComposeBindPreview,
  ComposeCandidateSummary,
  ComposePlanPreview,
  RefinementContext,
  RuntimeTargetSupport,
} from "@/features/_shared/channel-types";
import { summarizeActionFlowStateTopology } from "@/features/preview/lib/actionflow-state-topology";
import { usePreviewTestingStore } from "@/features/preview/store/preview-testing-store";
import {
  clearStoredAppGenerationJob,
  isDirectAppGenerationLifecycleReservation,
  readAppGenerationPersistenceSubject,
  readActiveStoredAppGenerationJob,
  readStoredAppGenerationJob,
  reserveStoredAppGenerationLifecyclePending,
  shouldClearStoredAppGenerationJobForServerState,
} from "@/services/block-api-service";
import { loadProject, probeProjectGenerationState } from "@/services/project-api";
import type { ActionFlowTraceEntry } from "@/features/preview/types";
import {
  useAskCogoHandler,
  useOpenValidatorTabHandler,
  useOpenOntologyPanelHandler,
} from "@/features/validation-bridge";
import {
  StudioGenerationBusyError,
  startAppGenerationFromPrompt,
} from "@/features/chat/services/start-app-generation-from-prompt";
import { surfaceStudioGenerationFailure } from "@/features/chat/services/studio-generation-failure-surface";
import { presentGenerationReadinessInChat } from "@/features/chat/services/present-generation-readiness";
import {
  GENERATION_PROJECT_RECOVERY_COPY,
  GenerationReadinessChatCard,
} from "@/features/chat/components/generation-readiness-chat-card";
import { PROJECT_CHAT_OWNED_MESSAGE_IDS } from "@/features/chat/components/use-project-chat-thread-hydration";
import { useGenerationNextActions } from "./use-generation-next-actions";
import { SkillCard } from "./skill-card";
import { TerminologySelectionCard } from "@/features/chat/components/terminology-selection-card";
import { PersistedMarkdownText } from "@/features/chat/components/markdown-text";
import {
  bindProjectChatGeneration,
  projectChatOutcomeIdForSourceMessage,
  requestProjectChatThreadRetry,
  settleBoundProjectChatGenerationOutcome,
  settleProjectChatThreadDescriptor,
  settleProjectChatThreadOutcome,
  type ProjectChatOutcomeStatus,
  type ProjectChatThreadScope,
} from "@/features/chat/project-chat-thread";
import {
  applyTerminologyScopeSelection,
  TERMINOLOGY_SCOPE_PATTERN,
} from "@/features/chat/services/terminology-scope-selection";
import { TemplateProposalExchange } from "@/features/templates/components/template-proposal-exchange";
import { TEMPLATE_REGISTRY } from "@/features/templates/registry";
import {
  cancelStudioCapability,
  confirmStudioCapability,
  getStudioCapabilityConfirmationLifecycle,
  listPendingStudioCapabilities,
  listStudioCapabilityExecutions,
  subscribeStudioCapabilityGateway,
  type StudioCapabilityExecution,
  undoStudioCapabilityExecution,
} from "@/features/chat/services/studio-capability-gateway";

/**
 * v2.2 Phase 029 — view-model AFTER the 2-second grouping memo. Non-realtime
 * kinds pass through verbatim; realtime-update kinds wrap in a synthetic
 * group (CONTEXT.md D-06 hard rule: useStudioStore.chatMessages NOT mutated).
 */
type RealtimeUpdate = Extract<
  ChatMessage,
  { kind: "mutation-realtime-update" }
>;
type RealtimeUpdateGroup = {
  kind: "mutation-realtime-update-group";
  id: string;
  count: number;
  lastTimestamp: number;
  latestLatencyMs: number;
  events: RealtimeUpdate[];
};
type GroupedChatItem =
  | Exclude<ChatMessage, RealtimeUpdate>
  | RealtimeUpdateGroup;

// Task 5 (Plan B3) — the terminology-selection card's "템플릿 용어" badge:
// every scope any TEMPLATE_REGISTRY entry's own terminologySurface declares.
// Computed once from the already-imported static registry (TEMPLATE_REGISTRY
// is used just below by the `template-proposal` case) rather than inside
// TerminologySelectionCard itself — that keeps the card a leaf UI component
// with no registry import of its own, so this "badge whether a candidate is
// template vocabulary" concern lives entirely at the render site.
const TEMPLATE_TERMINOLOGY_SCOPES: Set<string> = new Set(
  TEMPLATE_REGISTRY.map((entry) => entry.pkg.terminologySurface?.scope).filter(
    (scope): scope is string => typeof scope === "string" && scope.length > 0,
  ),
);

/**
 * Task 7 (slice 1) — visibility inversion for `progressOnly` (the compact/
 * shipping dock).
 *
 * BEFORE this task, `progressOnly` was an ALLOW-list at the render site:
 * `visibleMessages` filtered `groupedMessages` down to a hand-picked set of
 * kinds, so a NEW `ChatMessage` kind was invisible in every compact panel
 * the moment it shipped — until someone independently rediscovered it
 * missing and remembered to add it. This file's own history is the
 * evidence the mechanism doesn't work: `terminology-selection`,
 * `template-proposal`, and `generation-readiness` were each added to that
 * list one incident at a time, and the SAME audit that found
 * `validation-summary`/`validation-issue` missing (this task) also found
 * `mutation-applied` missing — a fifth, independent instance of the exact
 * same failure mode. A list that must be remembered once per incident is a
 * habit, not a mechanism.
 *
 * `CHAT_MESSAGE_VISIBILITY` inverts the default AND makes the inversion
 * resistant to the same rot: it is typed `Record<GroupedChatItem["kind"],
 * ChatVisibilityClass>`, so TypeScript requires an entry for EVERY kind in
 * the `ChatMessage`/`GroupedChatItem` union and rejects any key that is
 * not one. Adding a new message kind to the union without adding a line
 * here is a COMPILE ERROR, not a silent "renders nowhere in compact
 * panels". A human can still classify a kind wrong (pick 'outcome' when it
 * should be 'progress', or vice versa) — that mistake is still possible —
 * but forgetting to classify a kind at all is not: the build won't let a
 * new kind through unclassified the way it let mutation-applied and
 * validation-summary/-issue through invisible.
 *
 * WHAT 'progress' ACTUALLY MEANS — READ THIS BEFORE ADDING A KIND (Task 7
 * quality-review finding, not present in the first cut of this file):
 * `ChatMessages` is mounted in exactly two places in this codebase —
 * `chat-panel.tsx` and `ChatRail.tsx` — and BOTH pass `progressOnly`
 * unconditionally. Grep `<ChatMessages` under `src/` and check every call
 * site before assuming otherwise; there is no third one, and there is no
 * "full" (non-progressOnly) chat surface shipped anywhere in the product.
 * So `'progress'` does not mean "hidden in the compact panel, visible
 * elsewhere" — it means **never rendered as a chat message, anywhere, in
 * production.** A kind (or, for `ai-result-success` below, a specific
 * instance of a kind) classified `'progress'` is only acceptable when
 * something ELSE genuinely substitutes for it — not merely "this felt like
 * chatter." `ai-result-success` was originally classified `'progress'`
 * wholesale in this file's first version of this table; a review caught
 * that the substitute (`pageProgressFrame`) only exists for ONE of its
 * THREE producers (see the `ai-result-success` special case below) — the
 * other two left a successful chat-driven edit with zero acknowledgment
 * anywhere. Reclassifying an omission into an explicit, permanent-looking
 * table entry makes it LESS likely to be rediscovered than a stray allow-
 * list gap was, not more — so treat every `'progress'` entry as a claim
 * that needs its substitute named, not a default.
 *
 * 'outcome' — visible under progressOnly (i.e. visible, period — see above).
 * The record of what happened, or a decision the user must make. This is
 * the default a reader should reach for when adding a new kind: hiding a
 * genuine outcome is the exact failure this task exists to fix, and
 * mutation-failed joins mutation-applied here for the same reason
 * ai-result-failure already sat beside ai-result-success in the old list —
 * an outcome that failed is still an outcome, and hiding failures
 * specifically would be worse than hiding successes.
 * 'progress' — suppressed under progressOnly (i.e. suppressed everywhere —
 * see above). Process narration with a real substitute acknowledgment
 * elsewhere: either superseded within the same turn by a later 'outcome'
 * message (ai-channel-2-start → ai-result-success/-failure), or pure
 * pipeline telemetry with no content a user reads (ai-channel-1-hit).
 * `compose-preview` and grouped realtime echoes of OTHER users' writes were
 * not part of this task's defect and flipping them was not verified here —
 * unlike `ai-result-success`, no report has yet named a producer of either
 * that reaches the user with zero acknowledgment; that is the bar for
 * revisiting them, not merely "this task didn't check."
 */
type ChatVisibilityClass = "outcome" | "progress";

type ProjectChatOutcomeMessage = Extract<
  ChatMessage,
  { kind: "project-chat-outcome" }
>;

const PROJECT_CHAT_OUTCOME_COPY: Record<
  LocaleCode,
  {
    status: Record<ProjectChatOutcomeMessage["status"], string>;
    route: Record<ProjectChatOutcomeMessage["route"], string>;
    retry: string;
  }
> = {
  en: {
    status: {
      pending: "In progress",
      succeeded: "Completed",
      reverted: "Reverted",
      refused: "Could not proceed",
      "transport-failure": "Connection problem",
      failed: "Failed",
    },
    route: {
      "read-only": "Project status",
      "grounded-edit": "Project edit",
      generation: "App generation",
      "agent-runtime": "Bot",
      "studio-operation": "Studio",
    },
    retry: "Try again",
  },
  ko: {
    status: {
      pending: "진행 중",
      succeeded: "완료",
      reverted: "되돌림 완료",
      refused: "진행할 수 없음",
      "transport-failure": "연결 문제",
      failed: "실패",
    },
    route: {
      "read-only": "프로젝트 상태",
      "grounded-edit": "프로젝트 편집",
      generation: "앱 생성",
      "agent-runtime": "봇",
      "studio-operation": "Studio",
    },
    retry: "다시 시도",
  },
  ja: {
    status: {
      pending: "進行中",
      succeeded: "完了",
      reverted: "取り消し済み",
      refused: "実行できません",
      "transport-failure": "接続エラー",
      failed: "失敗",
    },
    route: {
      "read-only": "プロジェクト状態",
      "grounded-edit": "プロジェクト編集",
      generation: "アプリ生成",
      "agent-runtime": "ボット",
      "studio-operation": "Studio",
    },
    retry: "再試行",
  },
  zh: {
    status: {
      pending: "进行中",
      succeeded: "已完成",
      reverted: "已撤销",
      refused: "无法继续",
      "transport-failure": "连接问题",
      failed: "失败",
    },
    route: {
      "read-only": "项目状态",
      "grounded-edit": "项目编辑",
      generation: "应用生成",
      "agent-runtime": "机器人",
      "studio-operation": "Studio",
    },
    retry: "重试",
  },
  "zh-TW": {
    status: {
      pending: "進行中",
      succeeded: "已完成",
      reverted: "已還原",
      refused: "無法繼續",
      "transport-failure": "連線問題",
      failed: "失敗",
    },
    route: {
      "read-only": "專案狀態",
      "grounded-edit": "專案編輯",
      generation: "應用程式生成",
      "agent-runtime": "機器人",
      "studio-operation": "Studio",
    },
    retry: "重試",
  },
};

const CHAT_MESSAGE_VISIBILITY: Record<GroupedChatItem["kind"], ChatVisibilityClass> = {
  user: "outcome",
  "project-chat-outcome": "outcome",
  "ai-channel-1-hit": "progress",
  "ai-channel-2-start": "progress",
  "compose-preview": "progress",
  // Renders null unconditionally (see the "page-progress" render-switch
  // case) — classified for the exhaustiveness guarantee above, not because
  // this value is ever observed.
  "page-progress": "progress",
  // DEAD VALUE — never actually consulted. `isChatMessageVisibleInProgressOnly`
  // special-cases `ai-result-success` by `source` BEFORE reaching the map
  // lookup below (same shape as the `agent-worklog` exception), because
  // this kind's real visibility varies by PRODUCER, not by kind — see that
  // function's own comment. This entry exists only because
  // `Record<GroupedChatItem["kind"], ChatVisibilityClass>` requires one for
  // every kind; whichever value is written here has zero effect on runtime
  // behaviour.
  "ai-result-success": "progress",
  "ai-result-failure": "outcome",
  // DEAD VALUE — never actually consulted, for the same reason as
  // `ai-result-success` above: `isChatMessageVisibleInProgressOnly`
  // special-cases `agent-worklog` by `card` (see `WORKLOG_EVIDENCE_CARDS`
  // below) before this map is ever read for that kind. An earlier version
  // of this comment described this entry as "the default for a worklog
  // whose card is NOT one of those two" — that described a fallthrough
  // path that does not exist in the code; the real default for a
  // non-evidence card is hardcoded directly in the conditional below.
  "agent-worklog": "progress",
  "app-skill": "outcome",
  "validation-summary": "outcome",
  "validation-issue": "outcome",
  "mutation-applied": "outcome",
  "capability-confirmation": "outcome",
  "mutation-failed": "outcome",
  "mutation-realtime-update-group": "progress",
  "terminology-selection": "outcome",
  "template-proposal": "outcome",
  "generation-readiness": "outcome",
};

/**
 * `agent-worklog`'s card-level exception to `CHAT_MESSAGE_VISIBILITY`.
 * Keys off `card` — the card's IDENTITY — and never off whose language the
 * card speaks. It used to test `titleCode`, which the copy layer withheld
 * from developer-persona cards to stop them rendering bilingual; the effect
 * was that the developer's launch check vanished from the IDE chat rail.
 */
const WORKLOG_EVIDENCE_CARDS: ReadonlySet<WorklogCardId> = new Set([
  "webmcp-capabilities",
  "production-evidence",
  // Wave 4 lane 1 — the central bus-execution-seam disclosure card
  // (studio-mcp-bus.ts's `executeWithAuthority`). This is the SAME
  // mechanism the two cards above already use to reach the creator persona
  // despite `agent-worklog`'s default 'progress' (hidden-under-progressOnly)
  // classification — see this file's own comment above `CHAT_MESSAGE_VISIBILITY`
  // ("there is no 'full' chat surface shipped anywhere in the product").
  "agent-action",
]);

function isChatMessageVisibleInProgressOnly(message: GroupedChatItem): boolean {
  if (message.kind === "agent-worklog") {
    return Boolean(message.card && WORKLOG_EVIDENCE_CARDS.has(message.card));
  }
  // Task 7 quality-review finding — `ai-result-success` has exactly THREE
  // producers in this codebase (grep `kind: 'ai-result-success'` under
  // `src/`, excluding tests, to re-verify): `use-channel-dispatch.ts` fires
  // it twice (channel-1 hit, channel-2 rendered), and in BOTH calls it
  // ALSO calls `replacePageProgress(null)` in the very same block — there
  // is no substitute acknowledgment anywhere for a channel-dispatch
  // success. `register-studio-tools.ts`'s app-generation-complete path is
  // the ONLY producer that sets `source: 'app-generation'`, and it does so
  // alongside `replaceAppGenerationProgress(..., 'generated-app', ...)` —
  // which sets `pageProgressFrame`, rendered by `ChatPageProgress` a few
  // lines below `visibleMessages` in this component, UNCONDITIONALLY
  // (independent of `progressOnly` and independent of this message kind
  // entirely). So: `source === 'app-generation'` has a real substitute and
  // stays hidden (showing this bubble too would duplicate that substitute);
  // anything else (`'channel'`, or absent — both channel-dispatch call
  // sites omit `source`) has NO substitute and must be visible, or a
  // successful chat-driven edit reaches the user with zero acknowledgment.
  if (message.kind === "ai-result-success") {
    return message.source !== "app-generation";
  }
  return CHAT_MESSAGE_VISIBILITY[message.kind] === "outcome";
}

/**
 * F3 — the card's language comes from the SESSION, not from a regex over the
 * producer's English.
 *
 * What was here before: a title mapper that turned "WebMCP capabilities" into
 * the Korean "화면 제어" and "Production evidence" into the English "Launch
 * check" in EVERY locale; a label mapper that word-substituted a partial list
 * of nouns into Korean; and a value mapper that rewrote two of the five value
 * shapes. None took a locale, so one card carried two languages in an English
 * session and in a Korean one alike.
 *
 * A worklog a creator reads now carries a `titleCode` / entry `code` and this
 * file only looks the words up. An entry with no `code` is a raw
 * developer-persona technical line (`status`, `source refs`, `mode`) and is
 * deliberately rendered verbatim.
 */
function displayWorklogTitle(
  msg: {
    title: string;
    card?: WorklogCardId;
    entries: ReadonlyArray<{ code?: WorklogEntryCode }>;
  },
  copy: CoogoIdeChromeCopy,
): string {
  // The rule lives in the copy module (I1): a localized title over a verbatim
  // entry is a bilingual card, so the whole card keeps the producer's title.
  return worklogCardTitle(msg, copy);
}

interface WorklogEntryLike {
  label: string;
  value: string;
  code?: WorklogEntryCode;
  count?: number;
  total?: number;
}

function displayWorklogEntry(
  entry: WorklogEntryLike,
  copy: CoogoIdeChromeCopy,
): { label: string; value: string } {
  if (!entry.code) return { label: entry.label, value: entry.value };
  return copy.worklog.entries[entry.code]({
    count: entry.count ?? 0,
    total: entry.total,
  });
}

function humanizeTechnicalLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._/-]+/g, " ")
    .replace(/\bcogo json\b/gi, "project model")
    .trim();
}

/**
 * The exact path shape `pageMetaPaths()` emits
 * (`features/chat/services/studio-operation-impact-contract.ts`):
 * `/manifest/pages/<encoded pageId>/<title|description|route|layout>`.
 * Anchored end-to-end so a longer/other path never matches by accident.
 */
const PAGE_META_CHANGE_PATH =
  /^\/manifest\/pages\/[^/]+\/(title|description|route|layout)$/;

/**
 * P2 (finding 3): a page-title rename used to render as the raw field path
 * `App · manifest pages home title` — a JSON-pointer-shaped string standing
 * in for "what is changing". For the four page-meta fields the impact
 * registry itself declares, this returns the humanized, localized name it
 * already implies. Every other path keeps the previous behaviour verbatim.
 */
function capabilityChangeTargetLabel(
  change: { path: string; label: string },
  copy: ChatMessagesCopy,
): string {
  const match = PAGE_META_CHANGE_PATH.exec(change.path);
  if (match) {
    return copy.capability.changeTargets.pageMeta[
      match[1] as keyof ChatMessagesCopy["capability"]["changeTargets"]["pageMeta"]
    ];
  }
  return humanizeTechnicalLabel(change.label);
}

interface ChatMessagesProps {
  layout?: "fill" | "inline" | "story";
  hideWhenEmpty?: boolean;
  className?: string;
  progressOnly?: boolean;
  showActionFlowTrace?: boolean;
  /**
   * Creator-first drawer content that belongs behind the single progressive
   * disclosure. Omitted by the IDE ChatRail, preserving its existing layout.
   */
  workDetails?: React.ReactNode;
}

const WORK_DETAILS_COPY: Record<LocaleCode, string> = {
  en: "Work details",
  ko: "작업 세부 정보",
  ja: "作業の詳細",
  zh: "工作详情",
  "zh-TW": "工作詳細資料",
};

function isWorkDetailsMessage(message: GroupedChatItem): boolean {
  return (
    message.kind === "agent-worklog" ||
    message.kind === "compose-preview" ||
    message.kind === "mutation-realtime-update-group"
  );
}

function readExplicitStudioProjectId(): string | null {
  if (typeof window === "undefined") return null;
  const queryProjectId = new URLSearchParams(window.location.search).get(
    "projectId",
  );
  if (queryProjectId) return queryProjectId;
  const ideMatch = window.location.pathname.match(/^\/ide\/([^/]+)(?:\/|$)/);
  if (!ideMatch) return null;
  try {
    return decodeURIComponent(ideMatch[1]);
  } catch {
    return null;
  }
}

const GENERATION_RECOVERY_COPY = {
  en: {
    message: "Could not verify the saved app. Nothing was regenerated.",
    retry: "Retry saved app verification",
  },
  ko: {
    message: "저장된 앱을 확인할 수 없습니다. 앱을 다시 생성하지 않았습니다.",
    retry: "저장 상태 다시 확인",
  },
  ja: {
    message: "保存済みアプリを確認できませんでした。再生成は行っていません。",
    retry: "保存状態を再確認",
  },
  zh: {
    message: "无法验证已保存的应用。应用未被重新生成。",
    retry: "重新验证保存状态",
  },
  "zh-TW": {
    message: "無法驗證已儲存的應用程式。系統未重新產生應用程式。",
    retry: "重新驗證儲存狀態",
  },
} as const;

export function ChatMessages({
  layout = "fill",
  hideWhenEmpty = false,
  className = "",
  progressOnly = false,
  showActionFlowTrace = false,
  workDetails,
}: ChatMessagesProps = {}) {
  const { locale } = useStudioLocale();
  const generationRecoveryCopy = GENERATION_RECOVERY_COPY[locale];
  const messages = useStudioStore((s) => s.chatMessages);
  const pageProgress = useStudioStore((s) => s.pageProgressFrame);
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const currentProjectAppId = useStudioStore((s) => {
    const manifest = s.currentProject?.manifest as
      | Record<string, unknown>
      | undefined;
    return typeof manifest?.appId === "string" ? manifest.appId : null;
  });
  const latestActionFlowTrace = usePreviewTestingStore((s) =>
    showActionFlowTrace ? s.actionFlowTraces[0] : null,
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [generationRecoveryError, setGenerationRecoveryError] = useState<{
    projectId: string;
    requestId: string;
  } | null>(null);
  const [generationRecoveryAttempt, setGenerationRecoveryAttempt] = useState(0);

  // A page refresh clears both the ephemeral chat frame and the Home-started
  // project's in-memory selection, not the server job. The bounded active
  // pointer discovers exactly one subject, then Studio reloads and verifies
  // that server project before exposing any resume action.
  useEffect(() => {
    let cancelled = false;
    const hydrateActiveGeneration = async () => {
      let descriptor = readActiveStoredAppGenerationJob();
      if (!descriptor) return;
      const explicitProjectId = readExplicitStudioProjectId();
      if (explicitProjectId && explicitProjectId !== descriptor.projectId) {
        return;
      }
      // G1.4 ghost-job cleanup — reconcile the stored pointer against the
      // server durability marker BEFORE reviving any resume surface. A job
      // whose project is authoritatively gone (404/410) or whose generation
      // the server already marked complete clears instead of retrying; an
      // 'unknown' probe (transient failure) never invalidates the anchor.
      const serverProbe = await probeProjectGenerationState(
        descriptor.projectId,
      );
      if (cancelled) return;
      if (
        shouldClearStoredAppGenerationJobForServerState(descriptor, serverProbe)
      ) {
        const studio = useStudioStore.getState();
        const userId = studio.session?.user?.id;
        const generationCompleted =
          serverProbe.outcome === "exists" &&
          serverProbe.generationState === "complete";
        const generationSubjectMissing = serverProbe.outcome === "missing";
        if (userId && (generationCompleted || generationSubjectMissing)) {
          const completedGeneration = {
            projectId: descriptor.projectId,
            requestId: descriptor.requestId,
            jobId: descriptor.jobId,
          };
          const settled = settleBoundProjectChatGenerationOutcome(
            userId,
            completedGeneration,
            {
              status: generationCompleted ? "succeeded" : "failed",
              text: generationCompleted
                ? GENERATION_PROJECT_RECOVERY_COPY[locale].completed
                : GENERATION_PROJECT_RECOVERY_COPY[locale].failed,
              retryable: generationSubjectMissing,
              timestamp: Date.now(),
            },
          );
          const outcome = settled?.turns.find(
            (turn) =>
              turn.kind === "outcome" &&
              turn.generation?.projectId === completedGeneration.projectId &&
              turn.generation.requestId === completedGeneration.requestId &&
              (!completedGeneration.jobId ||
                turn.generation.jobId === completedGeneration.jobId),
          );
          const active = useStudioStore.getState();
          if (
            settled &&
            outcome?.kind === "outcome" &&
            settled.userId === userId &&
            active.session?.user?.id === settled.userId &&
            active.currentProjectId === settled.projectId
          ) {
            useStudioStore.setState({
              chatMessages: active.chatMessages.map((message) =>
                message.id === outcome.id
                  ? { ...outcome, kind: "project-chat-outcome" }
                  : message,
              ),
            });
          }
        }
        clearStoredAppGenerationJob(
          descriptor.projectId,
          descriptor.jobId,
          descriptor.requestId,
        );
        return;
      }
      setGenerationRecoveryError(null);

      let studio = useStudioStore.getState();
      if (
        studio.currentProjectId !== descriptor.projectId ||
        studio.projectSource !== "server" ||
        !studio.currentProject
      ) {
        await studio.openServerProject(descriptor.projectId, {
          navigate: false,
          activateWorkspace: false,
        });
        if (cancelled) return;
        studio = useStudioStore.getState();
      }

      const manifest = studio.currentProject?.manifest as
        | Record<string, unknown>
        | undefined;
      const loadedAppId =
        typeof manifest?.appId === "string" ? manifest.appId : null;
      if (
        studio.currentProjectId !== descriptor.projectId ||
        studio.projectSource !== "server" ||
        !studio.currentProject
      ) {
        return;
      }
      if (loadedAppId !== descriptor.appId) {
        // Only an exact successful project load may invalidate the subject.
        // A transient load failure must leave the durable reconnect anchor.
        // Conversely, a loaded mismatched app can never consume this job.
        clearStoredAppGenerationJob(
          descriptor.projectId,
          descriptor.jobId,
          descriptor.requestId,
        );
        const progressFrame = studio.pageProgressFrame;
        const progressBackgroundJob =
          progressFrame?.kind === "page-progress"
            ? progressFrame.backgroundJob
            : undefined;
        if (
          progressBackgroundJob?.projectId === descriptor.projectId &&
          progressBackgroundJob.jobId === descriptor.jobId
        ) {
          studio.replacePageProgress(null);
        }
        return;
      }
      if (studio.pageProgressFrame) return;
      let lifecyclePending = descriptor.status === "lifecycle-pending";
      if (
        !lifecyclePending &&
        isDirectAppGenerationLifecycleReservation(descriptor)
      ) {
        const persistedReadback = await loadProject(descriptor.projectId);
        if (cancelled) return;
        const persistedManifest = persistedReadback.app.manifest as unknown as
          | Record<string, unknown>
          | undefined;
        const persistedSubject = readAppGenerationPersistenceSubject(
          persistedReadback.settings,
        );
        const exactPersistenceSubject =
          persistedManifest?.appId === descriptor.appId &&
          persistedSubject?.projectId === descriptor.projectId &&
          persistedSubject.appId === descriptor.appId &&
          persistedSubject.appSpecFingerprint ===
            descriptor.appSpecFingerprint;
        const persistedBlocks = persistedReadback.app.blocks ?? {};
        const expectedBlockNames = descriptor.appSpec.blocks
          .map((block) => block.name?.trim())
          .filter((name): name is string => Boolean(name));
        if (
          !exactPersistenceSubject ||
          expectedBlockNames.length === 0 ||
          expectedBlockNames.some(
            (name) => !Object.prototype.hasOwnProperty.call(persistedBlocks, name),
          )
        ) {
          // A pre-persistence direct reservation is not a generation resume
          // capability. Remove it rather than regenerating an unproven app.
          clearStoredAppGenerationJob(
            descriptor.projectId,
            undefined,
            descriptor.requestId,
          );
          return;
        }
        descriptor =
          reserveStoredAppGenerationLifecyclePending(descriptor.appSpec) ??
          descriptor;
        lifecyclePending = true;
      }
      const pages = descriptor.appSpec.blocks.map((block) => ({
        id: block.name,
        name: block.name,
        status: lifecyclePending ? ("done" as const) : ("pending" as const),
      }));
      studio.replacePageProgress({
        kind: "page-progress",
        id: "app-generation-progress-frame",
        timestamp: Date.now(),
        pages:
          pages.length > 0
            ? pages
            : [
                {
                  id: descriptor.appId,
                  name: descriptor.appSpec.name,
                  status: lifecyclePending
                    ? ("done" as const)
                    : ("pending" as const),
                },
              ],
        elapsedMs: Math.max(0, Date.now() - descriptor.startedAt),
        backgroundJob: {
          schemaVersion: "studio-app-generation-background-progress.v1",
          projectId: descriptor.projectId,
          ...(descriptor.jobId ? { jobId: descriptor.jobId } : {}),
          status: "paused",
          stage: lifecyclePending
            ? "lifecycle-finalization-pending"
            : "reconnect-available",
          canResume: true,
          continuesInBackground: true,
          ...(descriptor.terminalSloMs
            ? { terminalSloMs: descriptor.terminalSloMs }
            : {}),
        },
      });
    };
    void hydrateActiveGeneration().catch(() => {
      if (cancelled) return;
      const descriptor = readActiveStoredAppGenerationJob();
      if (!descriptor) return;
      setGenerationRecoveryError({
        projectId: descriptor.projectId,
        requestId: descriptor.requestId,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [
    currentProjectAppId,
    currentProjectId,
    generationRecoveryAttempt,
    locale,
    pageProgress,
  ]);

  // v2.2 Phase 029 — collapse consecutive mutation-realtime-update events
  // within 2000ms (CONTEXT.md D-06). chatMessages is NEVER mutated.
  const groupedMessages = useMemo<GroupedChatItem[]>(() => {
    const out: GroupedChatItem[] = [];
    for (const m of messages ?? []) {
      if (m.kind === "mutation-realtime-update") {
        const tail = out[out.length - 1];
        if (
          tail &&
          tail.kind === "mutation-realtime-update-group" &&
          m.timestamp - tail.lastTimestamp <= 2000
        ) {
          tail.count += 1;
          tail.lastTimestamp = m.timestamp;
          tail.latestLatencyMs = m.latencyMs;
          tail.events.push(m);
          continue;
        }
        out.push({
          kind: "mutation-realtime-update-group",
          id: m.id,
          count: 1,
          lastTimestamp: m.timestamp,
          latestLatencyMs: m.latencyMs,
          events: [m],
        });
      } else {
        out.push(m);
      }
    }
    return out;
  }, [messages]);
  // progressOnly suppresses process narration in compact panels; see
  // `CHAT_MESSAGE_VISIBILITY`'s own comment (above `isChatMessageVisibleInProgressOnly`)
  // for the visibility contract and why it is an exhaustive, compile-checked
  // map rather than a growable allow-list.
  const visibleMessages = progressOnly
    ? groupedMessages.filter(isChatMessageVisibleInProgressOnly)
    : groupedMessages;
  const conversationMessages = workDetails
    ? visibleMessages.filter((message) => !isWorkDetailsMessage(message))
    : visibleMessages;
  const workDetailMessages = workDetails
    ? visibleMessages.filter(isWorkDetailsMessage)
    : [];

  // This list used to set `scrollTop = scrollHeight` on every change with no
  // check of where the reader was, so scrolling up to re-read something was
  // undone by the next arrival — every time. The shared hook follows the newest
  // message only while the reader is at the bottom, and Bot Mode's canvas, which
  // had no scroll handling at all, takes the same one rather than a copy of the
  // version with the defect in it.
  useMessageListAutoscroll(
    scrollRef,
    [
      conversationMessages.length,
      pageProgress,
      latestActionFlowTrace,
      generationRecoveryError,
    ],
    {
      // A trace with no messages under it is a list whose interesting end is
      // its first row.
      pin:
        latestActionFlowTrace && conversationMessages.length === 0 && !pageProgress
          ? "top"
          : "bottom",
    },
  );

  if (
    hideWhenEmpty &&
    visibleMessages.length === 0 &&
    !pageProgress &&
    !generationRecoveryError &&
    !latestActionFlowTrace &&
    !workDetails
  )
    return null;

  const layoutClass =
    layout === "fill"
      ? "flex-1"
      : layout === "story"
        ? "shrink-0"
        : "max-h-80 shrink-0";
  const overflowClass =
    layout === "story" ? "overflow-visible" : "overflow-y-auto";

  const renderMessage = (m: GroupedChatItem) =>
    m.kind === "mutation-realtime-update-group" ? (
      <GroupedRealtimeBubble key={m.id} group={m} />
    ) : (
      <MessageRow key={m.id} msg={m} />
    );

  const messageList = (
    <div
      ref={scrollRef}
      className={`${layoutClass} ${overflowClass} px-4 py-4 flex flex-col gap-3.5 ${className}`.trim()}
      data-testid="chat-messages"
      data-layout={layout}
    >
      {conversationMessages.map((m) =>
        m.kind === "mutation-realtime-update-group" ? (
          <GroupedRealtimeBubble key={m.id} group={m} />
        ) : (
          <MessageRow key={m.id} msg={m} />
        ),
      )}
      {generationRecoveryError && (
        <div
          className="rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
          data-testid="chatrail-generation-recovery-error"
          role="alert"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle
              aria-hidden
              className="mt-0.5 h-4 w-4 shrink-0"
              style={{ color: MUTED_TEXT }}
            />
            <div className="min-w-0 flex-1">
              <p style={{ color: INK_700 }}>
                {generationRecoveryCopy.message}
              </p>
              <button
                className="mt-2 inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium hover:bg-muted"
                data-testid="chatrail-generation-recovery-retry"
                onClick={() => {
                  setGenerationRecoveryError(null);
                  setGenerationRecoveryAttempt((attempt) => attempt + 1);
                }}
                type="button"
              >
                <RotateCcw aria-hidden className="h-3.5 w-3.5" />
                {generationRecoveryCopy.retry}
              </button>
            </div>
          </div>
        </div>
      )}
      {pageProgress && pageProgress.kind === "page-progress" && (
        <ChatPageProgress
          titleCode={pageProgress.titleCode}
          pages={pageProgress.pages}
          elapsedMs={pageProgress.elapsedMs}
          evidence={pageProgress.evidence}
          backgroundJob={pageProgress.backgroundJob}
        />
      )}
      {latestActionFlowTrace && !workDetails && (
        <ActionFlowTraceBubble trace={latestActionFlowTrace} />
      )}
    </div>
  );

  if (!workDetails) return messageList;

  return (
    <>
      {messageList}
      <details
        className="group shrink-0 border-t border-border/60 bg-card/30"
        data-testid="chat-work-details"
      >
        <summary className="flex min-h-10 cursor-pointer list-none items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
          <ChevronRight
            aria-hidden
            className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90"
          />
          <span>{WORK_DETAILS_COPY[locale]}</span>
        </summary>
        <div className="border-t border-border/60 bg-background">
          {workDetails}
          {workDetailMessages.length > 0 || latestActionFlowTrace ? (
            <div className="px-4 py-4 flex flex-col gap-3.5">
              {workDetailMessages.map(renderMessage)}
              {latestActionFlowTrace ? (
                <ActionFlowTraceBubble trace={latestActionFlowTrace} />
              ) : null}
            </div>
          ) : null}
        </div>
      </details>
    </>
  );
}

function compactValue(value: unknown, maxLength = 96): string {
  let text: string;
  if (value === undefined) text = "undefined";
  else if (typeof value === "string") text = value;
  else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
}

function ActionFlowTraceBubble({ trace }: { trace: ActionFlowTraceEntry }) {
  const { locale } = useStudioLocale();
  const copy = getChatMessagesCopy(locale);
  const visibleDiffs = trace.stateDiffs.slice(0, 4);
  const visibleActions = trace.actionCalls.slice(0, 3);
  const visibleSteps = trace.stepTrace.slice(0, 3);
  const resultNames = Object.keys(trace.results);
  const domains =
    trace.stateDomains.length > 0
      ? trace.stateDomains.join(", ")
      : copy.common.none;
  const topologySummary = summarizeActionFlowStateTopology(trace);

  return (
    <AiBubble
      icon={Activity}
      iconColor={MUTED_TEXT}
      surface="runtime-evidence"
      testId="actionflow-inspector"
      wide
    >
      <div className="min-w-0 space-y-2">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <strong className="studio-ellipsis min-w-0">{copy.actionFlowTrace.heading}</strong>
          <span
            className={
              trace.status === "pass"
                ? "shrink-0 text-foreground"
                : "shrink-0 text-destructive"
            }
          >
            {trace.status}
          </span>
        </div>
        <div
          className="rounded-md border border-border/70 bg-card/70 px-2 py-1.5 text-[11px]"
          data-testid="actionflow-state-topology-summary"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-foreground">{copy.actionFlowTrace.stateTopologyHeading}</span>
            <span className="font-mono text-[10px] text-muted-foreground">
              {copy.actionFlowTrace.pathsCount(topologySummary.changedPathCount)} · {trace.durationMs}ms
            </span>
          </div>
          <dl className="mt-1 grid grid-cols-[76px_minmax(0,1fr)] gap-x-2 gap-y-1">
            {topologySummary.rows.map((row) => (
              <div key={row.id} className="contents">
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd
                  className="studio-ellipsis-block font-mono text-foreground"
                  title={row.value}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
        <dl className="grid grid-cols-[48px_minmax(0,1fr)] gap-x-2 gap-y-1 text-[11px]">
          <dt className="text-muted-foreground">{copy.actionFlowTrace.event}</dt>
          <dd
            className="studio-ellipsis-block font-mono"
            title={trace.eventLabel}
          >
            {trace.eventLabel}
          </dd>
          <dt className="text-muted-foreground">{copy.actionFlowTrace.flow}</dt>
          <dd
            className="studio-ellipsis-block font-mono"
            title={trace.flowName}
          >
            {trace.flowName}
          </dd>
          <dt className="text-muted-foreground">{copy.actionFlowTrace.domains}</dt>
          <dd className="studio-ellipsis-block font-mono" title={domains}>
            {domains}
          </dd>
        </dl>
        {visibleSteps.length > 0 && (
          <div
            className="space-y-1"
            data-testid="actionflow-overlay-step-trace"
          >
            <div className="text-[11px] text-muted-foreground">{copy.actionFlowTrace.stepOrder}</div>
            {visibleSteps.map((step, itemIndex) => (
              <div
                key={`${step.index}:${step.path}:${itemIndex}`}
                className="grid grid-cols-[44px_minmax(0,1fr)] gap-2 rounded bg-background/70 px-2 py-1 text-[11px]"
              >
                <span className="font-mono text-foreground">
                  #{step.index}
                  {step.parentIndex !== undefined
                    ? ` <- ${step.parentIndex}`
                    : ""}
                </span>
                <span
                  className="studio-ellipsis-block font-mono text-muted-foreground"
                  title={`${step.action ?? step.type} / ${step.status}`}
                >
                  {step.action ?? step.type} / {step.status}
                </span>
              </div>
            ))}
          </div>
        )}
        {visibleActions.length > 0 && (
          <div className="space-y-1">
            {visibleActions.map((call, itemIndex) => (
              <div
                key={`${call.path}:${call.action}:${call.order ?? "unsequenced"}:${itemIndex}`}
                className="grid grid-cols-[72px_minmax(0,1fr)] gap-2 rounded bg-background/70 px-2 py-1 text-[11px]"
              >
                <span className="font-mono text-foreground">
                  {call.order !== undefined ? `#${call.order} ` : ""}
                  {call.action}
                </span>
                <span
                  className="studio-ellipsis-block font-mono text-muted-foreground"
                  title={`${call.stepType} / ${call.dataSource}${call.target ? ` / ${call.target}` : ""}`}
                >
                  {call.stepType} / {call.dataSource}
                  {call.target ? ` / ${call.target}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
        {visibleDiffs.length > 0 && (
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground">
              {copy.actionFlowTrace.stateDiffHeading}
            </div>
            {visibleDiffs.map((diff, itemIndex) => (
              <div
                key={`${diff.path}:${itemIndex}`}
                className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 rounded bg-background/70 px-2 py-1 text-[11px]"
              >
                <span
                  className="studio-ellipsis-block font-mono text-foreground"
                  title={diff.path}
                >
                  {diff.path}
                </span>
                <span
                  className="studio-ellipsis-block font-mono text-muted-foreground"
                  title={`${diff.domain}: ${compactValue(diff.before, 160)} -> ${compactValue(diff.after, 160)}`}
                >
                  {diff.domain}: {compactValue(diff.before)} {"->"}{" "}
                  {compactValue(diff.after)}
                </span>
              </div>
            ))}
          </div>
        )}
        {resultNames.length > 0 && (
          <div className="studio-ellipsis-block font-mono text-[11px] text-muted-foreground">
            {resultNames.map((name) => `_results.${name}`).join(", ")}
          </div>
        )}
        {trace.rendererProjection.length > 0 && (
          <div>
            <div className="text-[11px] text-muted-foreground">{copy.actionFlowTrace.renderer}</div>
            <div
              data-testid="actionflow-overlay-renderer-projection"
              className="mt-1 space-y-1"
            >
              {trace.rendererProjection.slice(0, 4).map((projection) => (
                <div
                  key={`${projection.elementId}:${projection.text}`}
                  className="grid grid-cols-[84px_minmax(0,1fr)] gap-2 rounded bg-background/70 px-2 py-1 text-[11px]"
                >
                  <span
                    className="studio-ellipsis-block font-mono text-muted-foreground"
                    title={projection.elementId}
                  >
                    {projection.elementId}
                  </span>
                  <span
                    className="studio-ellipsis-block font-mono text-foreground"
                    title={projection.text}
                  >
                    {projection.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {trace.error && (
          <div className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1 text-[11px] text-destructive">
            {compactValue(trace.error, 160)}
          </div>
        )}
      </div>
    </AiBubble>
  );
}

function formatScore(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function fmtDurationMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms.toFixed(0)}ms`;
}

function supportTone(
  support: RuntimeTargetSupport,
  copy: ChatMessagesCopy,
): {
  label: string;
  className: string;
} {
  if (support === "supported") {
    return {
      label: copy.compose.supported,
      className: "border-border bg-background text-muted-foreground",
    };
  }
  if (support === "partial") {
    return {
      label: copy.compose.partial,
      className: "border-border bg-background text-muted-foreground",
    };
  }
  return {
    label: copy.compose.unsupported,
    className: "border-destructive/30 bg-destructive/10 text-destructive",
  };
}

function joinOrNone(values: readonly string[], none: string): string {
  return values.length > 0 ? values.join(", ") : none;
}

function ComposePreviewCard({
  plan,
  candidates,
  bindPreview,
  developer = false,
  copy,
}: {
  plan: ComposePlanPreview;
  candidates: ComposeCandidateSummary[];
  bindPreview: ComposeBindPreview;
  /** F30 — "RAG plan" is engine vocabulary; show it only to developers. */
  developer?: boolean;
  copy: ChatMessagesCopy;
}) {
  const visibleCandidates = candidates.slice(0, 4);
  const overflow = candidates.length - visibleCandidates.length;
  return (
    <div className="min-w-0 space-y-3" data-testid="compose-preview-card">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <strong className="block studio-ellipsis">
            {developer ? copy.compose.ragPlanLabel : copy.compose.buildPlanLabel}
          </strong>
          <span
            className="block studio-ellipsis-block text-[11px] text-muted-foreground"
            title={plan.intent}
          >
            {plan.intent}
          </span>
        </div>
        <div className="shrink-0 rounded-md border border-border/70 bg-background/70 px-2 py-1 text-right">
          <div className="font-mono text-[11px] text-foreground">
            {plan.readyCandidateCount}/{plan.candidateCount}
          </div>
          <div className="text-[10px] text-muted-foreground">{copy.compose.readyLabel}</div>
        </div>
      </div>

      {/* persona-ux-01 — the card body is dense engine vocabulary (semanticType,
          patternRef IRIs, intent-token overlap, q-grade, evidence state, raw
          State/Actions/Data bind identifiers). Developers see the full plan;
          creators get a plain-language summary — how many sections will be
          built and how many need a closer look — with no RDF/ontology terms. */}
      {developer ? (
        <ComposePreviewDeveloperBody
          plan={plan}
          visibleCandidates={visibleCandidates}
          overflow={overflow}
          bindPreview={bindPreview}
          copy={copy}
        />
      ) : (
        <div
          className="space-y-1 text-[11px] text-muted-foreground"
          data-testid="compose-preview-creator-summary"
        >
          <div>{copy.compose.summary(plan.candidateCount, plan.blockedCandidateCount)}</div>
        </div>
      )}
    </div>
  );
}

function ComposePreviewDeveloperBody({
  plan,
  visibleCandidates,
  overflow,
  bindPreview,
  copy,
}: {
  plan: ComposePlanPreview;
  visibleCandidates: ComposeCandidateSummary[];
  overflow: number;
  bindPreview: ComposeBindPreview;
  copy: ChatMessagesCopy;
}) {
  return (
    <>
      <dl className="grid grid-cols-3 gap-1 text-[11px]">
        <div className="rounded bg-background/70 px-2 py-1">
          <dt className="text-muted-foreground">{copy.compose.pages}</dt>
          <dd className="font-mono text-foreground">{plan.pageCount}</dd>
        </div>
        <div className="rounded bg-background/70 px-2 py-1">
          <dt className="text-muted-foreground">{copy.compose.bindings}</dt>
          <dd className="font-mono text-foreground">
            {plan.stateBindingCount +
              plan.actionBindingCount +
              plan.datasourceBindingCount}
          </dd>
        </div>
        <div className="rounded bg-background/70 px-2 py-1">
          <dt className="text-muted-foreground">{copy.compose.blockedLabel}</dt>
          <dd className="font-mono text-foreground">
            {plan.blockedCandidateCount}
          </dd>
        </div>
      </dl>

      <div className="space-y-1">
        <div className="text-[11px] text-muted-foreground">
          {copy.compose.candidateBlocks}
        </div>
        {visibleCandidates.map((candidate) => (
          <div
            key={candidate.id}
            className={`rounded border px-2 py-1.5 text-[11px] ${
              candidate.status === "blocked"
                ? "border-destructive/30 bg-destructive/10"
                : "border-border/70 bg-background/70"
            }`}
            data-testid={`compose-candidate-${candidate.status}`}
          >
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span
                className="studio-ellipsis min-w-0 font-mono text-foreground"
                title={candidate.patternRef}
              >
                {candidate.semanticType}
              </span>
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 font-mono ${
                  candidate.status === "blocked"
                    ? "bg-destructive/10 text-destructive"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {candidate.status === "blocked"
                  ? copy.compose.blockedBadge
                  : formatScore(candidate.score)}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              <span
                className="rounded bg-muted/60 px-1.5 py-0.5 font-mono"
                title={
                  candidate.intentMatchDetail
                    ? `${candidate.intentMatchDetail.matched} of ${candidate.intentMatchDetail.total} intent tokens matched`
                    : undefined
                }
              >
                {/* Real-path candidates carry the raw token overlap — "2/14" is a
                    measured fact; "14%" reads like a quality score it is not. */}
                {candidate.intentMatchDetail
                  ? copy.compose.intentRatio(
                      candidate.intentMatchDetail.matched,
                      candidate.intentMatchDetail.total,
                    )
                  : copy.compose.intentScore(formatScore(candidate.intentMatch))}
              </span>
              {candidate.qualityGrade !== undefined && (
                <span className="rounded bg-muted/60 px-1.5 py-0.5 font-mono">
                  q{Math.round(candidate.qualityGrade)}
                </span>
              )}
              <span className="rounded bg-muted/60 px-1.5 py-0.5 font-mono">
                {/* 'unbound' = not bound (only the winner is), not looked-for-and-absent. */}
                {candidate.evidenceState === "unbound"
                  ? copy.compose.notBound
                  : copy.compose.evidenceState(candidate.evidenceState)}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {Object.entries(candidate.runtimeTargets).map(
                ([target, support]) => {
                  const tone = supportTone(support, copy);
                  return (
                    <span
                      key={target}
                      className={`rounded border px-1.5 py-0.5 font-mono ${tone.className}`}
                    >
                      {target} {tone.label}
                    </span>
                  );
                },
              )}
            </div>
            {candidate.blockReason && (
              <div className="mt-1 rounded bg-destructive/10 px-1.5 py-1 text-destructive">
                {candidate.blockReason}
              </div>
            )}
          </div>
        ))}
        {overflow > 0 && (
          <div className="font-mono text-[11px] text-muted-foreground">
            {copy.compose.moreCandidates(overflow)}
          </div>
        )}
      </div>

      <div className="rounded-md border border-border/70 bg-background/70 px-2 py-1.5 text-[11px]">
        <div className="mb-1 font-medium text-foreground">{copy.compose.bindPreviewHeading}</div>
        <dl className="grid grid-cols-[72px_minmax(0,1fr)] gap-x-2 gap-y-1">
          <dt className="text-muted-foreground">{copy.compose.stateLabel}</dt>
          <dd
            className="studio-ellipsis-block font-mono"
            title={joinOrNone(bindPreview.state, copy.common.none)}
          >
            {joinOrNone(bindPreview.state, copy.common.none)}
          </dd>
          <dt className="text-muted-foreground">{copy.compose.actionsLabel}</dt>
          <dd
            className="studio-ellipsis-block font-mono"
            title={joinOrNone(bindPreview.actions, copy.common.none)}
          >
            {joinOrNone(bindPreview.actions, copy.common.none)}
          </dd>
          <dt className="text-muted-foreground">{copy.compose.dataLabel}</dt>
          <dd
            className="studio-ellipsis-block font-mono"
            title={joinOrNone(bindPreview.datasources, copy.common.none)}
          >
            {joinOrNone(bindPreview.datasources, copy.common.none)}
          </dd>
        </dl>
        {bindPreview.warnings.length > 0 && (
          <div className="mt-1 rounded border border-border bg-muted/40 px-1.5 py-1 text-muted-foreground">
            {bindPreview.warnings[0]}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * F29 — remove literal "SHACL" engine vocabulary from a creator-facing
 * mutation-failed summary. `formatShaclMessage` emits "N SHACL violations · …"
 * / "SHACL validation failed …" in its multi/zero-violation branches; creators
 * should never read that token. The single-violation branch is already
 * jargon-free ("<focus> · <path> — <message>") and passes through untouched.
 */
function stripShaclJargon(summary: string): string {
  return summary
    .replace(/\bSHACL validation failed\b/gi, "Validation failed")
    .replace(/\bSHACL\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function formatCapabilityValue(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Canonical definition lives in chat-messages-copy.ts (`ChatCapabilityCardStatus`)
 * so the locale table's status-keyed records stay structurally tied to this
 * union without a circular import. */
type CapabilityCardStatus = ChatCapabilityCardStatus;

type CapabilityRollbackState = {
  attempted: boolean;
  succeeded: boolean;
  error?: string;
};

type CapabilityCardState = {
  status: CapabilityCardStatus;
  executionId?: string;
  undoAvailable?: boolean;
  rollback?: CapabilityRollbackState;
  /**
   * The LOCALIZED primary failure line. P2 (finding 2): this used to be the
   * gateway's raw `error` string, which is how
   * `studio-orchestration-node-handlers.ts:431`'s
   * `'The approved exact impact is stale.'` reached a Korean operator's
   * recovery card verbatim. It is now always resolved through
   * `resolveStudioConfirmationFailureCopy` — the same table
   * `studio-task-center.tsx` has always used on its own confirm path.
   */
  error?: string;
  /**
   * The raw engine message, preserved as secondary diagnostic detail (a
   * `title` tooltip), never as the primary line — the contract
   * `ResolvedStudioExecutionErrorCopy` already states for `detail`.
   */
  errorDetail?: string;
};

// Terminology selection UI plan (Task 3) — post-action state for the
// terminology-selection card. C2 (bootstrap-only cross-scope candidate
// surfacing) split the former single 'confirmed' status into two, because
// an app-scope pick and a platform-scope pick now do genuinely different
// things and I1 requires the copy to say only what actually happened:
//   - 'declared': the pick was an app:<slug>-scope candidate, a project
//     write was attempted (in-memory always, persist best-effort — see
//     applyTerminologyScopeSelection), and future prompts stay in that
//     scope.
//   - 'phrasing-only': the pick was a platform-scope candidate. No store or
//     persist write ever happens for this branch (fixes I3) — it is purely
//     a suggestion to rephrase using the picked defined term.
// 'dismissed' replaces the card with a neutral notice. See
// terminologySelectionOutcome in MessageRow.
//
// I4: this state is intentionally MessageRow-local (not lifted to the
// store/message itself), so a re-render from a stale message array resets
// it to 'pending' and could, in principle, "resurrect" an already-acted-on
// card. That's harmless post-I2/I3: I2 purges every terminology-selection
// message on project switch (the only realistic resurrect path), a
// platform pick never wrote anything to resurrect, and re-declaring the
// same app scope in applyTerminologyScopeSelection is an idempotent no-op
// (and a DIFFERENT scope is refused outright — scope-conflict).
type TerminologySelectionOutcome =
  | { status: "pending" }
  | { status: "declared"; term: string; candidateId: string; scope: string }
  | { status: "phrasing-only"; term: string; candidateId: string }
  | { status: "dismissed" };

// The template-proposal card's state machine moved with the card, into
// `features/templates/components/template-proposal-exchange.tsx` — Home shows
// the same exchange and there was no honest way to keep its rules here.

const CAPABILITY_DETAIL_PREVIEW_LIMIT = 4;
const CAPABILITY_BUTTON_FOCUS_CLASS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

function capabilityChangeCount(
  changedPaths: string[],
  impactChanges: Array<{ path: string }> = [],
): number {
  const knownPaths = new Set(
    [...changedPaths, ...impactChanges.map((change) => change.path)].filter(
      (path) => path.trim().length > 0,
    ),
  );
  return knownPaths.size;
}

function capabilityCardStateFromExecution(
  execution: StudioCapabilityExecution,
  executionCopy: StudioExecutionCopy,
  genericFailure: string,
): CapabilityCardState {
  const localized = execution.error
    ? (resolveStudioConfirmationFailureCopy(executionCopy, {
        executionFailureCode: execution.executionFailureCode,
      }) ?? genericFailure)
    : undefined;
  const shared = {
    executionId: execution.executionId,
    undoAvailable: execution.undoAvailable,
    rollback: execution.rollback,
    ...(localized ? { error: localized } : {}),
    ...(execution.error ? { errorDetail: execution.error } : {}),
  };
  switch (execution.status) {
    case "executed":
      return { ...shared, status: "executed" };
    case "failed":
      return {
        ...shared,
        status:
          execution.undoAvailable || execution.rollback?.succeeded === false
            ? "recovery-needed"
            : "failed",
      };
    case "undone":
      return { ...shared, status: "undone" };
    case "cancelled":
      return { ...shared, status: "cancelled" };
    case "running":
    case "suspended":
      return { ...shared, status: "executing" };
  }
}

function capabilityStatusCopy(
  count: number,
  status: CapabilityCardStatus,
  copy: ChatMessagesCopy,
): string {
  if (count === 0) {
    return copy.capability.statusZero[status] ?? copy.capability.statusZero.default;
  }
  const changes = copy.capability.statusWithCount.changeCount(count);
  switch (status) {
    case "cancelling":
      return copy.capability.statusWithCount.cancelling(changes);
    case "executing":
      return copy.capability.statusWithCount.applying(changes);
    case "executed":
      return copy.capability.statusWithCount.applied(changes);
    case "cancelled":
      return copy.capability.statusWithCount.cancelled(changes);
    case "failed":
      return copy.capability.statusWithCount.failed(changes);
    case "recovery-needed":
      return copy.capability.statusWithCount.recoveryNeeded(changes);
    case "undo-failed":
      return copy.capability.statusWithCount.undoFailed(changes);
    case "undone":
      return copy.capability.statusWithCount.undone(changes);
    default:
      return copy.capability.statusWithCount.default(changes);
  }
}

function creatorPendingReadbackCopy(
  impact: { operationId: string | null; readback: string },
  copy: ChatMessagesCopy,
): string {
  const operationId = impact.operationId ?? "";
  if (/token|credential|secret/i.test(`${operationId} ${impact.readback}`)) {
    return copy.capability.pendingReadback.secret;
  }
  if (/^(release\.|project\.publish|project\.deploy)/.test(operationId)) {
    return copy.capability.pendingReadback.release;
  }
  if (/(^|\.)(export|bundle|download)(\.|$)/.test(operationId)) {
    return copy.capability.pendingReadback.exportBundle;
  }
  if (/(^|\.)(import|ingest)(\.|$)/.test(operationId)) {
    return copy.capability.pendingReadback.importIngest;
  }
  if (/(^|\.)(open|navigate)(\.|$)/.test(operationId)) {
    return copy.capability.pendingReadback.openNavigate;
  }
  if (/(^|\.)(validate|test|inspect|review|readback)(\.|$)/.test(operationId)) {
    return copy.capability.pendingReadback.validateReview;
  }
  return copy.capability.pendingReadback.fallback;
}

function creatorCapabilityReadbackCopy(
  impact: { operationId: string | null; readback: string },
  status: CapabilityCardStatus,
  copy: ChatMessagesCopy,
): string {
  switch (status) {
    case "cancelling":
      return copy.capability.readback.cancelling;
    case "executing":
      return copy.capability.readback.executing;
    case "executed":
      return copy.capability.readback.executed;
    case "cancelled":
      return copy.capability.readback.cancelled;
    case "failed":
      return copy.capability.readback.failed;
    case "recovery-needed":
      return copy.capability.readback.recoveryNeeded;
    case "undo-failed":
      return copy.capability.readback.undoFailed;
    case "undone":
      return copy.capability.readback.undone;
    default:
      return creatorPendingReadbackCopy(impact, copy);
  }
}

function creatorCapabilityUndoCopy(
  undo: string,
  status: CapabilityCardStatus,
  undoAvailable: boolean | undefined,
  copy: ChatMessagesCopy,
): string {
  if (status === "cancelled") {
    return copy.capability.undo.nothingApplied;
  }
  if (status === "cancelling") {
    return copy.capability.undo.cancelling;
  }
  if (status === "failed") {
    return copy.capability.undo.failed;
  }
  if (status === "recovery-needed") {
    return undoAvailable
      ? copy.capability.undo.recoveryAvailable
      : copy.capability.undo.recoveryUnavailable;
  }
  if (status === "undo-failed") {
    return copy.capability.undo.undoFailed;
  }
  if (status === "undone") {
    return copy.capability.undo.undone;
  }
  if (status === "executing") {
    return copy.capability.undo.executing;
  }
  if (status === "executed") {
    return undoAvailable
      ? copy.capability.undo.executedAvailable
      : copy.capability.undo.executedUnavailable;
  }
  if (/no exact inverse|separate explicit change/i.test(undo)) {
    return copy.capability.undo.noInverse;
  }
  if (/cannot be undone locally|compensat|rollback/i.test(undo)) {
    return copy.capability.undo.cannotUndoLocally;
  }
  return copy.capability.undo.default;
}

function developerCapabilityReadbackCopy(
  readback: string,
  status: CapabilityCardStatus,
  rollback: CapabilityRollbackState | undefined,
  copy: ChatMessagesCopy,
): string {
  switch (status) {
    case "cancelling":
      return copy.capability.developerReadback.cancelling(readback);
    case "executing":
      return copy.capability.developerReadback.executing(readback);
    case "executed":
      return copy.capability.developerReadback.executed(readback);
    case "cancelled":
      return copy.capability.developerReadback.cancelled(readback);
    case "failed":
      return copy.capability.developerReadback.failed(readback);
    case "recovery-needed":
      return copy.capability.developerReadback.recoveryNeeded(
        readback,
        rollback?.attempted
          ? copy.capability.developerReadback.rollbackDidNotComplete
          : copy.capability.developerReadback.rollbackWasUnavailable,
      );
    case "undo-failed":
      return copy.capability.developerReadback.undoFailed(readback);
    case "undone":
      return copy.capability.developerReadback.undone(readback);
    default:
      return readback;
  }
}

function developerCapabilityUndoCopy(
  undo: string,
  status: CapabilityCardStatus,
  undoAvailable: boolean | undefined,
  rollback: CapabilityRollbackState | undefined,
  copy: ChatMessagesCopy,
): string {
  switch (status) {
    case "cancelling":
      return copy.capability.developerUndo.cancelling(undo);
    case "executing":
      return copy.capability.developerUndo.executing(undo);
    case "executed":
      return undoAvailable
        ? copy.capability.developerUndo.executedAvailable(undo)
        : copy.capability.developerUndo.executedUnavailable(undo);
    case "cancelled":
      return copy.capability.developerUndo.cancelled(undo);
    case "failed":
      return copy.capability.developerUndo.failed(undo);
    case "recovery-needed": {
      const rollbackPhrase = rollback?.attempted
        ? copy.capability.developerUndo.rollbackFailedPhrase
        : copy.capability.developerUndo.rollbackWasUnavailablePhrase;
      return undoAvailable
        ? copy.capability.developerUndo.recoveryAvailable(undo, rollbackPhrase)
        : copy.capability.developerUndo.recoveryUnavailable(undo, rollbackPhrase);
    }
    case "undo-failed":
      return copy.capability.developerUndo.undoFailed(undo);
    case "undone":
      return copy.capability.developerUndo.undone(undo);
    default:
      return undo;
  }
}

/**
 * The element the creator has picked, in the ONE shape every refinement
 * consumer already takes (`RefinementContext`). Chip first — `chatRailChip` is
 * what the build-mode canvas writes on an element click (build-mode-canvas.tsx
 * `handleElementClick`, the "선택됨"/"Selected for chat" affordance) — then the
 * broader `chatContext` selection, which the property/page surfaces write.
 * `undefined` means nothing is selected, and every caller must keep working.
 */
function readSelectionRefinementContext(): RefinementContext | undefined {
  const state = useStudioStore.getState();
  const chip = state.chatRailChip;
  if (chip) {
    return {
      element_id: chip.element_id,
      element_type: chip.element_type,
      current_state: {},
      block_id: chip.block_id,
    };
  }
  const context = state.chatContext;
  if (context.selectedElementId && context.selectedBlockName) {
    return {
      element_id: context.selectedElementId,
      element_type: context.selectedElementType ?? "element",
      current_state: {
        pageId: context.pageId,
        selectionSurface: context.selectionSurface,
        propertyKey: context.selectedPropertyKey,
      },
      block_id: context.selectedBlockName,
    };
  }
  return undefined;
}

/**
 * Re-run an `ambiguous-target` request through the planner that REFUSED it,
 * this time carrying the element the creator picked.
 *
 * Why this exists — measured 2026-08-19. `ambiguous-target` is produced by the
 * LOCAL EDIT planner (`submitIntentChunkPlan` → `resolveElementTarget`), and
 * that planner is the ONLY consumer of a `RefinementContext` anywhere in the
 * app. `ChannelInput` used to carry a `refinementContext` field written by
 * `use-channel-dispatch.ts` and read by NOTHING — neither `route-channel.ts`
 * nor channel-1 nor channel-2 mentioned it. So the previous retry, which handed
 * the freshly made selection to `dispatch()`, wrote it into a name nobody
 * honours: the channel router saw a bare intent, missed in channel 1, and
 * generated a whole new app in channel 2 (`FALLBACK_PAGE_NAMES` —
 * Login/Dashboard/Tasks/Settings/Profile for a Korean request about one signup
 * field). The card told the creator that selecting a target would change the
 * outcome; it could not. The field is now gone and `dispatch` takes the intent
 * alone, so this branch is the only way a selection changes a retry's outcome.
 *
 * The transcript shape deliberately mirrors what `dispatch()` produces on a
 * retry (the intent echoed as a user turn first) and what the composer produces
 * for the same planner outcomes, so the two entries stay indistinguishable to a
 * reader of the thread.
 */
async function retryAmbiguousTargetWithSelection(
  intent: string,
  refinementContext: RefinementContext,
): Promise<void> {
  const store = useStudioStore.getState();
  store.appendChatMessage({
    kind: "user",
    id: crypto.randomUUID(),
    text: intent,
    timestamp: Date.now(),
  });
  try {
    const result = await submitIntentChunkPlan(intent, { refinementContext });
    if (result.handled) {
      useStudioStore.getState().appendChatMessage({
        kind: "mutation-applied",
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        tool: "modify_action",
        insertedCount: 0,
        subjects:
          result.changedPaths ??
          result.plan?.actions.flatMap(
            (action) => action.expectedChangedPaths,
          ) ??
          [],
        auditSource: "webmcp",
      });
      return;
    }
    if (
      result.blocker === "requires-user-confirmation" &&
      result.confirmationId &&
      result.confirmationExpiresAt
    ) {
      useStudioStore.getState().appendChatMessage({
        kind: "capability-confirmation",
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        confirmationId: result.confirmationId,
        title: result.analysis.summary,
        tools: result.executedTools ?? [],
        changedPaths: result.changedPaths ?? [],
        expiresAt: result.confirmationExpiresAt,
        impact: result.confirmationImpact,
      });
      return;
    }
    // Still not plannable WITH the target named. Say so and keep the intent
    // bound to the bubble so the creator can pick a different element — do NOT
    // fall through to whole-app generation, which is the silent wrong answer
    // this repair exists to remove.
    useStudioStore.getState().appendChatMessage({
      kind: "ai-result-failure",
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      reason: result.blocker ?? LOCAL_EDIT_PLAN_UNAVAILABLE_REASON,
      intent,
      source: "channel",
    });
  } catch {
    useStudioStore.getState().appendChatMessage({
      kind: "ai-result-failure",
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      reason: LOCAL_EDIT_PLAN_UNAVAILABLE_REASON,
      intent,
      source: "channel",
    });
  }
}

function MessageRow({ msg }: { msg: ChatMessage }) {
  const { locale, strings } = useStudioLocale()
  const chromeCopy = COOGO_IDE_CHROME_COPY[locale]
  const executionCopy = getStudioExecutionCopy(locale)
  const chatCopy = getChatMessagesCopy(locale)
  const askCogoToFix = useAskCogoHandler()
  const openValidatorTab = useOpenValidatorTabHandler()
  const openOntologyPanel = useOpenOntologyPanelHandler() // v2.2 Phase 029
  const { openPreview, openProjectOntology, openDeploy, openCode } = useGenerationNextActions()
  // F29 — raw RDF vocabulary (triple counts, IRI chips, SHACL lines, tool ids)
  // is developer-persona-only; creators get task-first/summary copy (F15 idiom,
  // mirrors validator-panel.tsx:27 / block-validation-inspector.tsx:92).
  const developer = useStudioStore((s) => s.studioPersona === "developer");
  // Later dispatches replace the whole block set, so old validation-issue
  // bubbles can reference blocks that left the project — their Ask-cogo
  // buttons render disabled instead of pretending to work (F19).
  const projectBlocks = useStudioStore((s) => s.currentProject?.blocks);
  const clearDispatch = useStudioStore((s) => s.clearDispatch);
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem);
  const setChatSidebarOpen = useStudioStore((s) => s.setChatSidebarOpen);
  const setStudioMode = useStudioStore((s) => s.setStudioMode);
  // isRunning gates the Retry button so a click during an in-flight generation
  // can't abort() the running run and start a competing one (CA-1).
  const { dispatch, isReady, isRunning } = useChannelDispatch();
  const appendChatMessage = useStudioStore((s) => s.appendChatMessage);
  const [durableConfirmationScope] = useState<ProjectChatThreadScope | null>(
    () => {
      if (msg.kind !== "capability-confirmation") return null;
      const studio = useStudioStore.getState();
      const userId = studio.session?.user?.id;
      const projectId = studio.currentProjectId;
      return userId && projectId ? { userId, projectId } : null;
    },
  );
  const [durableOutcomeScope] = useState<ProjectChatThreadScope | null>(() => {
    if (
      msg.kind !== "project-chat-outcome" &&
      msg.kind !== "template-proposal"
    ) return null;
    const studio = useStudioStore.getState();
    const userId = studio.session?.user?.id;
    const projectId = studio.currentProjectId;
    return userId && projectId ? { userId, projectId } : null;
  });
  const settleDurableConfirmation = (
    status: Exclude<ProjectChatOutcomeStatus, "pending">,
    text: string,
    retryable: boolean,
  ) => {
    if (!durableConfirmationScope) return;
    const outcomeId = projectChatOutcomeIdForSourceMessage(msg.id);
    const timestamp = Date.now();
    const settled = settleProjectChatThreadOutcome(
      durableConfirmationScope,
      outcomeId,
      { status, text, retryable, timestamp },
    );
    const outcome = settled?.turns.find(
      (turn) => turn.kind === "outcome" && turn.id === outcomeId,
    );
    if (!outcome || outcome.kind !== "outcome") return;
    const studio = useStudioStore.getState();
    if (
      studio.session?.user?.id !== durableConfirmationScope.userId ||
      studio.currentProjectId !== durableConfirmationScope.projectId
    ) {
      return;
    }
    useStudioStore.setState({
      chatMessages: studio.chatMessages.map((message) =>
        message.kind === "project-chat-outcome" && message.id === outcomeId
          ? { ...outcome, kind: "project-chat-outcome" }
          : message,
      ),
    });
  };
  const [capabilityState, setCapabilityState] = useState<CapabilityCardState>({
    status: "pending",
  });
  const [typedConfirmation, setTypedConfirmation] = useState("");
  const [showAllCapabilityDetails, setShowAllCapabilityDetails] =
    useState(false);
  // Terminology selection UI plan (Task 3) — post-action state for the
  // terminology-selection EXCEPTION-path card, local to this MessageRow
  // instance (mirrors capabilityState's own local-state pattern above: one
  // MessageRow per message, keyed by msg.id, so this persists across
  // re-renders without a store-level message-update mechanism).
  const [terminologySelectionOutcome, setTerminologySelectionOutcome] =
    useState<TerminologySelectionOutcome>({ status: "pending" });
  const cancellationInFlightRef = useRef(false);
  const capabilityStatusRef = useRef<HTMLDivElement>(null);
  const previousCapabilityStatusRef = useRef<CapabilityCardStatus>(
    capabilityState.status,
  );
  const capabilityConfirmationId =
    msg.kind === "capability-confirmation" ? msg.confirmationId : null;

  useEffect(() => {
    if (!capabilityConfirmationId) return;

    const synchronizeCapabilityState = () => {
      const lifecycle = getStudioCapabilityConfirmationLifecycle(
        capabilityConfirmationId,
      );
      const isPending = listPendingStudioCapabilities().some(
        (item) => item.confirmationId === capabilityConfirmationId,
      );
      const execution = [...listStudioCapabilityExecutions()]
        .reverse()
        .find(
          (item) => item.approval.confirmationId === capabilityConfirmationId,
        );
      if (execution) {
        // A durable Step 5 run is projected as a suspended execution while
        // its human gate is still awaiting this exact confirmation. Keep the
        // card actionable only when the matching pending authority remains;
        // an absent/mismatched pending entry stays fail-closed as executing.
        // An explicit non-pending lifecycle also wins, preventing a stale
        // suspended projection from reopening confirmation during execution.
        if (
          execution.status === "suspended" &&
          isPending &&
          (!lifecycle || lifecycle.status === "pending")
        ) {
          setCapabilityState({ status: "pending" });
          return;
        }
        setCapabilityState(
          capabilityCardStateFromExecution(
            execution,
            executionCopy,
            chatCopy.capability.confirmFailedGeneric,
          ),
        );
        return;
      }

      if (lifecycle) {
        switch (lifecycle.status) {
          case "pending":
            setCapabilityState({ status: "pending" });
            return;
          case "executing":
            setCapabilityState({ status: "executing" });
            return;
          case "executed":
            setCapabilityState({
              status: "executed",
              executionId: lifecycle.executionId,
              undoAvailable: false,
            });
            return;
          case "cancelled":
            setCapabilityState({ status: "cancelled" });
            return;
          case "failed":
            setCapabilityState({
              status: "failed",
              error: lifecycle.error,
            });
            return;
        }
      }

      if (isPending) {
        setCapabilityState({ status: "pending" });
        return;
      }
    };

    synchronizeCapabilityState();
    return subscribeStudioCapabilityGateway(synchronizeCapabilityState);
  }, [capabilityConfirmationId]);

  useEffect(() => {
    const previousStatus = previousCapabilityStatusRef.current;
    previousCapabilityStatusRef.current = capabilityState.status;
    if (previousStatus === capabilityState.status) return;

    // Confirm/Cancel/Undo/Recover buttons are replaced by terminal-state UI.
    // Move focus to the atomic status instead of allowing it to fall back to
    // <body>, so keyboard and assistive-technology users hear the result and
    // retain a stable position inside the confirmation card.
    capabilityStatusRef.current?.focus();
  }, [capabilityState.status]);

  // Lane 3 fix — recovery for a terminal ai-result-failure, bound PER BUBBLE.
  // Each failure carries the intent + source pipeline that produced it (set at
  // the producer), so Retry re-runs the EXACT failed request through the right
  // entry — never the globally-latest user message, never the wrong pipeline.
  //  • channel    + retryable → re-dispatch through the channel router.
  //  • app-generation + retryable → re-invoke the full-app generation entry.
  //  • non-retryable (permanent miss) OR no bound intent → clear and let the
  //    user re-prompt ('Start over' means a fresh start, not the same doomed run).
  const retryFailedGeneration = (
    failure: Extract<ChatMessage, { kind: "ai-result-failure" }>,
  ) => {
    const intent = failure.intent?.trim();
    const humanized =
      failure.source === "app-generation"
        ? humanizeAppGenerationFailureReason(failure.reason, locale)
        : (humanizeChatRequestBlocker(failure.reason, locale) ??
          humanizeFailureReason(failure.reason, locale));
    if (intent && humanized.retryable) {
      if (failure.source === "app-generation") {
        // Task 5.5 — a retry click is a fresh generation intent, so it goes
        // through the same presentation-first path as the original submit:
        // readiness panel in the thread first (its state may have changed
        // since the failure), and this bubble's pre-existing start block
        // runs only on the panel's generate click.
        void presentGenerationReadinessInChat({
          prompt: intent,
          proceed: (confirmedPrompt) => {
            void startAppGenerationFromPrompt(confirmedPrompt).catch((error: unknown) => {
              if (error instanceof StudioGenerationBusyError) {
                useStudioStore
                  .getState()
                  .setPendingChatNotice(error.userMessage);
                return;
              }
              // A retry that fails deserves the same durable failure bubble the
              // first attempt got — the transient notice this used to set expired
              // after 8s and left the thread claiming only the original failure.
              surfaceStudioGenerationFailure({ error, intent, locale });
            });
          },
        });
        return;
      }
      if (failure.source === "channel") {
        const refinementContext = readSelectionRefinementContext();
        // The card for this reason instructs the creator to select the exact
        // part and try again. Honour that instruction literally: send the retry
        // back to the LOCAL EDIT planner that raised `ambiguous-target`, which
        // is the only thing that reads a RefinementContext. Routing it through
        // `dispatch()` (the channel router) discards the selection — see
        // `retryAmbiguousTargetWithSelection` for the measured census.
        //
        // NOT gated on `isReady`: `isReady` is the knowledge-graph gate for the
        // channel router, and the local planner reads the editor/studio stores
        // only. A creator who has just followed the on-screen instruction must
        // not be sent to "Start over" because a KG init is still in flight.
        if (failure.reason === AMBIGUOUS_TARGET_REASON && refinementContext) {
          void retryAmbiguousTargetWithSelection(intent, refinementContext);
          return;
        }
        // Every other channel failure re-runs the whole request through the
        // router, which has no element-scoped step to hand a selection to — its
        // render replaces the project. The selection read above is for the
        // branch that can act on it, and stops here.
        if (isReady) {
          dispatch(intent);
          return;
        }
      }
    }
    // Non-retryable, unbound, or workspace-not-ready → Start over: clear the
    // failed output so the user can enter a new prompt. Focusing the composer
    // is best-effort via the pending-message channel (consumed by the composer).
    clearDispatch();
  };

  const selectTargetInApp = () => {
    setStudioMode("build");
    setActiveRailItem("preview");
    setChatSidebarOpen(true);
  };

  switch (msg.kind) {
    case "user":
      return (
        <div
          className="flex justify-end"
          data-testid="chatrail-message-user"
          data-collaboration-role="user"
        >
          <div className="max-w-[78%] break-words rounded-[14px] rounded-br border border-slate-950/10 bg-slate-950 px-3.5 py-2.5 text-[13.5px] leading-[1.45] text-white shadow-sm dark:border-slate-50/20 dark:bg-slate-50 dark:text-slate-950">
            {msg.text}
          </div>
        </div>
      );
    case "project-chat-outcome": {
      const outcomeCopy = PROJECT_CHAT_OUTCOME_COPY[locale];
      return (
        <AiBubble
          icon={
            msg.status === "succeeded" || msg.status === "reverted"
              ? CheckCircle2
              : msg.status === "pending"
                ? Clock
                : AlertTriangle
          }
          testId="chatrail-project-chat-outcome"
        >
          <span className="flex min-w-0 flex-col gap-1.5">
            <span className="min-w-0 break-words">
              <PersistedMarkdownText text={msg.text} />
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">
              {outcomeCopy.status[msg.status]} · {outcomeCopy.route[msg.route]}
            </span>
            {msg.retry.retryable ? (
              <button
                type="button"
                className="w-fit text-left text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => {
                  if (!durableOutcomeScope) return;
                  requestProjectChatThreadRetry({
                    scope: durableOutcomeScope,
                    sourceOutcomeId: msg.id,
                    retry: msg.retry,
                  });
                }}
              >
                {outcomeCopy.retry} · {msg.retry.intent}
              </button>
            ) : null}
          </span>
        </AiBubble>
      );
    }
    case "app-skill":
      return (
        <AiBubble
          icon={BookOpen}
          iconColor={INK_500}
          role="app"
          testId="chatrail-message-app-skill"
          wide
        >
          <SkillCard appName={msg.appName} skill={msg.skill} />
        </AiBubble>
      );
    case "capability-confirmation": {
      const capabilityTitleId = `capability-title-${msg.id}`;
      const capabilityDetailsId = `capability-details-${msg.id}`;
      const capabilityProvenanceWarningId = `capability-provenance-warning-${msg.id}`;
      const displayedChangedPaths = Array.from(
        new Set([
          ...msg.changedPaths,
          ...(msg.impact?.changes.map((change) => change.path) ?? []),
        ]),
      );
      const impactChanges = msg.impact?.changes ?? [];
      const visibleImpactChanges = showAllCapabilityDetails
        ? impactChanges
        : impactChanges.slice(0, CAPABILITY_DETAIL_PREVIEW_LIMIT);
      const visibleChangedPaths = showAllCapabilityDetails
        ? displayedChangedPaths
        : displayedChangedPaths.slice(0, CAPABILITY_DETAIL_PREVIEW_LIMIT);
      const hasHiddenCapabilityDetails =
        impactChanges.length > CAPABILITY_DETAIL_PREVIEW_LIMIT ||
        displayedChangedPaths.length > CAPABILITY_DETAIL_PREVIEW_LIMIT;
      const changeCount = capabilityChangeCount(
        msg.changedPaths,
        msg.impact?.changes,
      );
      const hasExactChanges = changeCount > 0;
      const confirm = async () => {
        setCapabilityState({ status: "executing" });
        const result = await confirmStudioCapability(
          msg.confirmationId,
          undefined,
          typedConfirmation,
        );
        if (result.status !== "executed") {
          const failureText =
            (result.status === "failed"
              ? resolveStudioConfirmationFailureCopy(executionCopy, result)
              : null) ?? chatCopy.capability.confirmFailedGeneric;
          if (result.status === "failed" && result.execution) {
            const recoveryNeeded =
              result.execution.undoAvailable ||
              result.execution.rollback?.succeeded === false;
            setCapabilityState({
              status: recoveryNeeded ? "recovery-needed" : "failed",
              executionId: result.execution.executionId,
              undoAvailable: result.execution.undoAvailable,
              rollback: result.execution.rollback,
              error:
                failureText,
              ...(result.error ? { errorDetail: result.error } : {}),
            });
          } else {
            setCapabilityState({
              status: "failed",
              error:
                failureText,
              ...(result.status === "failed" && result.error
                ? { errorDetail: result.error }
                : {}),
            });
          }
          settleDurableConfirmation("failed", failureText, true);
          return;
        }
        setCapabilityState({
          status: "executed",
          executionId: result.execution.executionId,
          undoAvailable: result.execution.undoAvailable,
        });
        appendChatMessage({
          kind: "mutation-applied",
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          tool: "modify_action",
          insertedCount: 0,
          subjects: result.execution.changedPaths,
          auditSource: "webmcp",
        });
        settleDurableConfirmation(
          "succeeded",
          chatCopy.mutation.appliedVerb.modify_action,
          false,
        );
      };
      const cancel = async (): Promise<boolean> => {
        if (cancellationInFlightRef.current) return false;
        cancellationInFlightRef.current = true;
        setCapabilityState({ status: "cancelling" });
        try {
          const cancelled = await cancelStudioCapability(msg.confirmationId);
          if (cancelled) {
            setCapabilityState({ status: "cancelled" });
            settleDurableConfirmation(
              "succeeded",
              chatCopy.capability.readback.cancelled,
              false,
            );
            return true;
          } else {
            setCapabilityState({
              status: "pending",
              error: chatCopy.capability.cancelUnavailable,
            });
            return false;
          }
        } catch (error) {
          const errorText =
            (error instanceof Error ? error.message : String(error)).trim() ||
            chatCopy.capability.cancelUnavailable;
          setCapabilityState({
            status: "pending",
            error: errorText,
          });
          return false;
        } finally {
          cancellationInFlightRef.current = false;
        }
      };
      const requestChanges = async () => {
        const cancelled = await cancel();
        if (!cancelled) return;
        const changeSummary = impactChanges
          .map((change) => capabilityChangeTargetLabel(change, chatCopy))
          .filter((label, index, labels) => labels.indexOf(label) === index)
          .join(", ");
        const authority = msg.impact?.currentState;
        const draft = [
          `${chatCopy.capability.actions.requestChanges}: ${msg.title}`,
          `Studio confirmation: ${msg.confirmationId}`,
          authority?.projectId ? `Project: ${authority.projectId}` : "",
          authority?.fingerprint
            ? `Base state: ${authority.fingerprint}`
            : "",
          changeSummary,
        ]
          .filter(Boolean)
          .join("\n");
        window.dispatchEvent(
          new CustomEvent("cogo:focus-chat-input", { detail: { draft } }),
        );
      };
      const undo = async () => {
        if (!capabilityState.executionId) return;
        const result = await undoStudioCapabilityExecution(
          capabilityState.executionId,
        );
        setCapabilityState(
          result.undone
            ? { status: "undone" }
            : {
                status: "undo-failed",
                executionId: capabilityState.executionId,
                undoAvailable: true,
                error: result.error,
              },
        );
        if (result.undone) {
          settleDurableConfirmation(
            "reverted",
            chatCopy.capability.undo.undone,
            false,
          );
        }
      };
      return (
        <AiBubble
          icon={ShieldCheck}
          iconColor={INK_500}
          role="app"
          testId="chatrail-message-capability-confirmation"
          wide
        >
          <div
            className="space-y-3"
            role="group"
            aria-labelledby={capabilityTitleId}
          >
            <div>
              <div
                id={capabilityTitleId}
                className="text-sm font-semibold text-foreground"
              >
                {msg.title}
              </div>
              <div
                ref={capabilityStatusRef}
                className="mt-1 rounded-sm text-xs text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:ring-offset-2 focus:ring-offset-background"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                tabIndex={-1}
              >
                <div>
                  {capabilityStatusCopy(changeCount, capabilityState.status, chatCopy)}
                </div>
                {capabilityState.error ? (
                  <div
                    className="mt-1 text-destructive"
                    data-testid="capability-error"
                    title={capabilityState.errorDetail}
                  >
                    {capabilityState.error}
                  </div>
                ) : null}
                {capabilityState.status === "failed" &&
                capabilityState.rollback?.succeeded === true ? (
                  <div
                    className="mt-1 text-foreground"
                    data-testid="capability-rollback-restored"
                  >
                    {executionCopy.rollback.completed.detail}
                  </div>
                ) : null}
                {/*
                  P2 (finding 2) — the symmetric branch. The "automatic
                  rollback failed" signal used to reach the reader ONLY as a
                  substring of the gateway's raw English `error` line; once
                  that line became localized-generic, the fact itself would
                  have been silently dropped. It is now stated on its own, in
                  the reader's language, from the same rollback state the
                  restored-branch above reads.
                */}
                {capabilityState.rollback?.attempted &&
                capabilityState.rollback.succeeded === false ? (
                  <div
                    className="mt-1 text-destructive"
                    data-testid="capability-rollback-failed"
                  >
                    {executionCopy.rollback.failureFallback}
                  </div>
                ) : null}
              </div>
            </div>
            <div id={capabilityDetailsId} className="space-y-3">
              {msg.impact ? (
                <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-foreground">
                    <span className="capitalize">{msg.impact.risk}</span>
                    {msg.impact.targetLabel ? (
                      <span className="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">
                        {msg.impact.targetLabel}
                      </span>
                    ) : null}
                  </div>
                  {visibleImpactChanges.map((change) => (
                    <div
                      key={change.path}
                      className="mt-2 border-t border-border/70 pt-2"
                      data-testid="capability-impact-change"
                    >
                      <div className="font-medium text-foreground [overflow-wrap:anywhere]">
                        {capabilityChangeTargetLabel(change, chatCopy)}
                      </div>
                      {change.before !== undefined ||
                      change.proposed !== undefined ? (
                        <div className="mt-1 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-muted-foreground">
                          {change.before !== undefined ? (
                            <>
                              <span>{chatCopy.capability.beforeLabel}</span>
                              <span
                                className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]"
                                data-testid="capability-change-before"
                              >
                                {formatCapabilityValue(change.before)}
                              </span>
                            </>
                          ) : null}
                          {change.proposed !== undefined ? (
                            <>
                              <span>{chatCopy.capability.afterLabel}</span>
                              <span
                                className="min-w-0 whitespace-pre-wrap text-foreground [overflow-wrap:anywhere]"
                                data-testid="capability-change-after"
                              >
                                {formatCapabilityValue(change.proposed)}
                              </span>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ))}
                  <div className="mt-2 space-y-2 border-t border-border/70 pt-2 text-muted-foreground">
                    <div>
                      <span className="font-medium text-foreground">
                        {chatCopy.capability.verificationLabel}
                      </span>{" "}
                      {developer
                        ? developerCapabilityReadbackCopy(
                            msg.impact.readback,
                            capabilityState.status,
                            capabilityState.rollback,
                            chatCopy,
                          )
                        : creatorCapabilityReadbackCopy(
                            msg.impact,
                            capabilityState.status,
                            chatCopy,
                          )}
                    </div>
                    <div>
                      <span className="font-medium text-foreground">{chatCopy.capability.undoLabel}</span>{" "}
                      {developer
                        ? developerCapabilityUndoCopy(
                            msg.impact.undo,
                            capabilityState.status,
                            capabilityState.undoAvailable,
                            capabilityState.rollback,
                            chatCopy,
                          )
                        : creatorCapabilityUndoCopy(
                            msg.impact.undo,
                            capabilityState.status,
                            capabilityState.undoAvailable,
                            chatCopy,
                          )}
                    </div>
                  </div>
                </div>
              ) : null}
              {displayedChangedPaths.length > 0 ? (
                <div className="space-y-1 border-l border-border pl-3 text-xs text-muted-foreground">
                  {visibleChangedPaths.map((path) => (
                    <div
                      key={path}
                      className="[overflow-wrap:anywhere]"
                      data-testid="capability-changed-path"
                    >
                      {humanizeTechnicalLabel(path)}
                    </div>
                  ))}
                </div>
              ) : null}
              {developer ? (
                <div
                  className="font-mono text-[10px] text-muted-foreground [overflow-wrap:anywhere]"
                  data-testid="capability-tools"
                >
                  {msg.tools.join(", ")}
                </div>
              ) : null}
            </div>
            {hasHiddenCapabilityDetails ? (
              <button
                type="button"
                data-testid="capability-details-toggle"
                aria-expanded={showAllCapabilityDetails}
                aria-controls={capabilityDetailsId}
                onClick={() => setShowAllCapabilityDetails((shown) => !shown)}
                className={`inline-flex h-8 items-center rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted ${CAPABILITY_BUTTON_FOCUS_CLASS}`}
              >
                {showAllCapabilityDetails
                  ? chatCopy.capability.actions.showFewerChanges
                  : chatCopy.capability.actions.showAllChanges(changeCount)}
              </button>
            ) : null}
            {!hasExactChanges ? (
              <p
                id={capabilityProvenanceWarningId}
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs leading-5 text-destructive"
                role="note"
              >
                {chatCopy.capability.missingChangesWarning}
              </p>
            ) : null}
            {capabilityState.status === "pending" &&
            msg.impact?.confirmation === "typed" ? (
              <label className="block text-xs font-medium text-foreground">
                {chatCopy.capability.typeToConfirmHint}
                <code
                  data-testid="capability-confirm-phrase"
                  className="mt-1.5 block select-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm text-foreground [overflow-wrap:anywhere]"
                >
                  {msg.impact.confirmationPhrase ?? ""}
                </code>
                <input
                  type="text"
                  value={typedConfirmation}
                  onChange={(event) => setTypedConfirmation(event.target.value)}
                  className="mt-1.5 h-9 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              {capabilityState.status === "pending" ? (
                <>
                  <button
                    type="button"
                    // Slice 2, task 4 added this testid because the control's
                    // only handle was its (then hard-coded English) accessible
                    // name. Fix round (post-review) finished the job: the
                    // label below is now locale-keyed
                    // (chatCopy.capability.actions.confirm), and every
                    // resolving call site in chat-messages.test.tsx moved to
                    // `getByTestId('capability-confirm')` so localizing this
                    // button no longer breaks them. Same pattern applied to
                    // its four siblings (cancel/undo/recover/retry-undo) and
                    // the details-toggle below.
                    data-testid="capability-confirm"
                    onClick={() => void confirm()}
                    aria-describedby={
                      hasExactChanges
                        ? undefined
                        : capabilityProvenanceWarningId
                    }
                    disabled={
                      !hasExactChanges ||
                      (msg.impact?.confirmation === "typed" &&
                        typedConfirmation.trim() !==
                          msg.impact.confirmationPhrase)
                    }
                    className={`inline-flex min-h-11 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-semibold text-background disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-8 ${CAPABILITY_BUTTON_FOCUS_CLASS}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {chatCopy.capability.actions.confirm}
                  </button>
                  <button
                    type="button"
                    data-testid="capability-cancel"
                    onClick={cancel}
                    className={`inline-flex min-h-11 items-center rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted sm:min-h-8 ${CAPABILITY_BUTTON_FOCUS_CLASS}`}
                  >
                    {chatCopy.capability.actions.cancel}
                  </button>
                  <button
                    type="button"
                    data-testid="capability-request-changes"
                    onClick={() => void requestChanges()}
                    className={`inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted sm:min-h-8 ${CAPABILITY_BUTTON_FOCUS_CLASS}`}
                  >
                    <PencilLine className="h-3.5 w-3.5" aria-hidden />
                    {chatCopy.capability.actions.requestChanges}
                  </button>
                </>
              ) : null}
              {capabilityState.status === "executing" ? (
                <span className="text-xs text-muted-foreground">
                  {chatCopy.capability.statusSpans.applying}
                </span>
              ) : null}
              {capabilityState.status === "cancelling" ? (
                <span className="text-xs text-muted-foreground">
                  {chatCopy.capability.statusSpans.cancelling}
                </span>
              ) : null}
              {capabilityState.status === "executed" ? (
                <>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {chatCopy.capability.statusSpans.applied}
                  </span>
                  {capabilityState.undoAvailable ? (
                    <button
                      type="button"
                      data-testid="capability-undo"
                      onClick={() => void undo()}
                      className={`inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted ${CAPABILITY_BUTTON_FOCUS_CLASS}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> {chatCopy.capability.actions.undo}
                    </button>
                  ) : null}
                </>
              ) : null}
              {capabilityState.status === "cancelled" ? (
                <span className="text-xs text-muted-foreground">{chatCopy.capability.statusSpans.cancelled}</span>
              ) : null}
              {capabilityState.status === "recovery-needed" ? (
                <>
                  <span className="text-xs font-medium text-destructive">
                    {chatCopy.capability.statusSpans.needsRecovery}
                  </span>
                  {capabilityState.undoAvailable ? (
                    <button
                      type="button"
                      data-testid="capability-recover"
                      onClick={() => void undo()}
                      className={`inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted ${CAPABILITY_BUTTON_FOCUS_CLASS}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> {chatCopy.capability.actions.recover}
                    </button>
                  ) : null}
                </>
              ) : null}
              {capabilityState.status === "undone" ? (
                <span className="text-xs text-muted-foreground">{chatCopy.capability.statusSpans.undone}</span>
              ) : null}
              {capabilityState.status === "undo-failed" ? (
                <>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {chatCopy.capability.statusSpans.applied}
                  </span>
                  <button
                    type="button"
                    data-testid="capability-retry-undo"
                    onClick={() => void undo()}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted ${CAPABILITY_BUTTON_FOCUS_CLASS}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> {chatCopy.capability.actions.retryUndo}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </AiBubble>
      );
    }
    case "agent-worklog": {
      const worklogRole = inferWorklogRole({
        actor: msg.actor,
        title: msg.title,
        stage: msg.stage,
      });
      return (
        <AiBubble
          icon={Activity}
          iconColor="currentColor"
          role={worklogRole}
          testId={
            msg.card === "agent-action"
              ? "chatrail-message-agent-action-disclosure"
              : "chatrail-message-agent-worklog"
          }
        >
          <span className="flex flex-col gap-1">
            <strong>{displayWorklogTitle(msg, chromeCopy)}</strong>
            <span className="flex flex-wrap gap-1">
              {msg.entries.map((entry) => {
                const role = entry.role ?? entryRoleFromTone(entry.tone);
                const tone = collaborationTone(role);
                const shown = displayWorklogEntry(entry, chromeCopy);
                return (
                  <span
                    key={`${entry.label}:${entry.value}`}
                    data-collaboration-role={role}
                    className="inline-flex min-w-0 max-w-full items-center gap-1 rounded px-0.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                    title={`${shown.label}: ${shown.value}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.bar}`}
                      aria-hidden
                    />
                    <span className="shrink-0 text-muted-foreground">
                      {shown.label}:&nbsp;
                    </span>
                    <span className="studio-ellipsis min-w-0 max-w-[13rem]">
                      {shown.value}
                    </span>
                  </span>
                );
              })}
            </span>
          </span>
        </AiBubble>
      );
    }
    case "compose-preview":
      return (
        <AiBubble
          icon={ListChecks}
          iconColor={INK_500}
          testId="chatrail-compose-preview"
          wide
        >
          <ComposePreviewCard
            plan={msg.plan}
            candidates={msg.candidates}
            bindPreview={msg.bindPreview}
            developer={developer}
            copy={chatCopy}
          />
        </AiBubble>
      );
    case "ai-channel-1-hit":
      return (
        <AiBubble icon={Zap}>
          {chatCopy.ai.foundInstantly}
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 11,
              color: MUTED_TEXT,
              marginLeft: 6,
            }}
          >
            ({msg.lookupMs.toFixed(0)}ms)
          </span>
        </AiBubble>
      );
    case "ai-channel-2-start":
      return (
        <AiBubble icon={Clock} testId="chatrail-message-streaming-start">
          {chatCopy.ai.creatingApp(msg.pageCount, Math.round(msg.estimateMs / 1000))}
        </AiBubble>
      );
    case "ai-result-success": {
      const appGenerationSuccess = msg.source === "app-generation";
      return (
        <AiBubble
          icon={CheckCircle2}
          testId="chatrail-message-ai-result-success"
        >
          <span className="flex min-w-0 flex-col gap-2">
            <span>
              {appGenerationSuccess ? chatCopy.ai.appReady : chatCopy.ai.applied}{" "}
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: MUTED_TEXT,
                }}
              >
                (
                {msg.totalMs >= 1000
                  ? `${(msg.totalMs / 1000).toFixed(1)}s`
                  : `${msg.totalMs.toFixed(0)}ms`}
                )
              </span>
            </span>
            {msg.stages?.length || msg.speedStats ? (
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: MUTED_TEXT,
                }}
                data-testid="chatrail-generation-speed-stats"
              >
                {(msg.stages ?? [])
                  .map((s) => `${s.label} ${fmtDurationMs(s.ms)}`)
                  .join(" · ")}
                {msg.stages?.length && msg.speedStats ? " · " : ""}
                {msg.speedStats
                  ? `p50 ${fmtDurationMs(msg.speedStats.p50Ms)} · p95 ${fmtDurationMs(msg.speedStats.p95Ms)} (n=${msg.speedStats.count})`
                  : ""}
              </span>
            ) : null}
            <span
              className="flex flex-wrap gap-1.5"
              data-testid="chatrail-generation-next-actions"
            >
              <GenerationActionButton
                icon={Eye}
                label={chromeCopy.surfaceLabels.preview}
                testId="chatrail-action-open-preview"
                onClick={openPreview}
              />
              <GenerationActionButton
                icon={Network}
                label={chromeCopy.surfaceLabels.library}
                testId="chatrail-action-open-library"
                onClick={openProjectOntology}
              />
              <GenerationActionButton
                icon={Rocket}
                label={chromeCopy.surfaceLabels.deploy}
                testId="chatrail-action-open-deploy"
                onClick={openDeploy}
              />
              {appGenerationSuccess && developer && (
                <GenerationActionButton
                  icon={Code2}
                  label={chromeCopy.surfaceLabels.code}
                  testId="chatrail-action-open-code"
                  onClick={openCode}
                />
              )}
            </span>
          </span>
        </AiBubble>
      );
    }
    case "ai-result-failure": {
      const appGenerationRecovery =
        msg.source === "app-generation"
          ? humanizeAppGenerationFailureReason(msg.reason, locale)
          : null;
      // Chat-request blockers (local planner declined / an open document is
      // not a generation source) carry curated, localized copy in BOTH
      // personas: unlike the app-generation codes there is no raw engine prose
      // behind them, so a developer gains nothing from seeing the bare code —
      // and the bare code (`knowledge-gap`) is exactly what used to leak into
      // the bubble title.
      const chatRequestBlocker = humanizeChatRequestBlocker(msg.reason, locale);
      const humanized =
        chatRequestBlocker ??
        (appGenerationRecovery && !developer
          ? appGenerationRecovery
          : humanizeFailureReason(msg.reason, locale));
      const retryable =
        chatRequestBlocker?.retryable ??
        appGenerationRecovery?.retryable ??
        humanized.retryable;
      // F15/F29/L3-C1 — the producer `details` is RAW engine prose ("Ontology
      // candidate <id> rejected: …", "Corpus … below rendered design-quality
      // floor", raw `err.message`/"TypeError: …"); it is developer-persona-only.
      // Creators get ONLY the humanizer's own safe detail (the SHACL page id),
      // never the producer string — the humanized title above carries the
      // creator-facing meaning. Mirrors the mutation-applied/-failed gates.
      const detailLine = developer
        ? (msg.details ?? humanized.detail)
        : humanized.detail;
      return (
        <AiBubble icon={AlertTriangle}>
          <span className="flex min-w-0 flex-col gap-2">
            <span className="min-w-0 break-words">{humanized.title}</span>
            {detailLine ? (
              // The detail line is creator-facing recovery PROSE ("Start a new
              // project without importing a source…"), not an identifier — it
              // must be readable in full. `studio-ellipsis` (white-space:
              // nowrap) truncated it to a single 647px line that overflowed the
              // ~320px chat panel, so it wraps instead. `overflow-wrap:anywhere`
              // also covers the developer-persona branch, where `msg.details`
              // is raw engine prose that can carry long unbroken ids/URLs.
              <span
                className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: MUTED_TEXT,
                }}
                title={detailLine}
              >
                {detailLine}
              </span>
            ) : null}
            <span className="flex flex-wrap gap-1.5">
              {msg.reason === AMBIGUOUS_TARGET_REASON ? (
                <GenerationActionButton
                  icon={MousePointer2}
                  label={chromeCopy.selectInApp}
                  testId="chatrail-failure-select-target"
                  onClick={selectTargetInApp}
                />
              ) : null}
              <GenerationActionButton
                icon={RotateCcw}
                label={
                  retryable
                    ? chromeCopy.tryAgain
                    : chromeCopy.startOver
                }
                testId="chatrail-failure-retry"
                onClick={() => retryFailedGeneration(msg)}
                disabled={isRunning}
              />
            </span>
          </span>
        </AiBubble>
      );
    }
    case "page-progress":
      return null;
    case "validation-summary": {
      // Task 7 (slice 1) — 'unreadable' (use-dispatch-validation-bridge.ts's
      // isReadableReport guard) has no violation/pass counts worth trusting;
      // it gets its own icon and copy rather than falling through the
      // pass/fail/warn branches below. Hard-coded Korean regardless of
      // locale is a pre-existing pattern on THIS surface, not a new gap:
      // the generation-readiness panel already hard-codes Korean copy
      // unconditionally (see step ①'s spec header for the measured
      // precedent) — this disclosure follows the same convention rather
      // than inventing a third one.
      if (msg.status === "unreadable") {
        return (
          <AiBubble
            icon={AlertTriangle}
            testId="chatrail-message-validation-summary"
          >
            검증 결과를 읽을 수 없습니다. 화면을 새로고침한 뒤 다시
            확인해 주세요.
          </AiBubble>
        );
      }
      const Icon: LucideIcon =
        msg.status === "pass"
          ? CheckCircle2
          : msg.status === "fail"
            ? AlertTriangle
            : AlertCircle;
      return (
        <AiBubble icon={Icon} testId="chatrail-message-validation-summary">
          {msg.violations === 0 ? (
            <>
              {chatCopy.validation.passedAcross(msg.pass, msg.blockCount)}
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: MUTED_TEXT,
                  marginLeft: 6,
                }}
              >
                (
                {msg.durationMs >= 1000
                  ? `${(msg.durationMs / 1000).toFixed(1)}s`
                  : `${msg.durationMs.toFixed(0)}ms`}
                )
              </span>
            </>
          ) : (
            <>
              <strong>
                {chatCopy.validation.violations(msg.violations)}
              </strong>
              {msg.warnings > 0 && (
                <>
                  {chatCopy.validation.warningsSuffix(msg.warnings)}
                </>
              )}
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: MUTED_TEXT,
                  marginLeft: 6,
                }}
              >
                (
                {msg.durationMs >= 1000
                  ? `${(msg.durationMs / 1000).toFixed(1)}s`
                  : `${msg.durationMs.toFixed(0)}ms`}
                )
              </span>
            </>
          )}
        </AiBubble>
      );
    }
    case "validation-issue": {
      if (msg.isOverflow) {
        return (
          <AiBubble
            icon={ExternalLink}
            testId="chatrail-message-validation-issue"
          >
            <button
              type="button"
              onClick={openValidatorTab}
              className="bg-transparent p-0 text-foreground underline underline-offset-2"
            >
              {chatCopy.validation.moreViolations(msg.remainingCount)}
            </button>
          </AiBubble>
        );
      }
      const blockGone = !projectBlocks?.[msg.blockId];
      return (
        <AiBubble icon={Circle} testId="chatrail-message-validation-issue">
          <strong>
            {msg.blockName}
            {msg.elementId ? ` / ${msg.elementId}` : ""}
          </strong>
          : {msg.message}
          <button
            type="button"
            onClick={() => askCogoToFix(msg)}
            disabled={blockGone}
            title={blockGone ? chatCopy.validation.blockGoneTitle : undefined}
            style={{
              marginLeft: 8,
              padding: "2px 8px",
              background: blockGone
                ? "hsl(var(--muted-foreground) / 0.25)"
                : PURPLE,
              color: "hsl(var(--primary-foreground))",
              border: "none",
              borderRadius: 4,
              fontSize: 11,
              cursor: blockGone ? "not-allowed" : "pointer",
              textDecoration: blockGone ? "line-through" : "none",
            }}
          >
            {chatCopy.mutation.tryFix}
          </button>
        </AiBubble>
      );
    }
    case "mutation-applied": {
      // v2.2 Phase 029 — successful cogo.ontology.{add_block,modify_action,merge_branch}
      // Phase 032 Plan 05 — testid `chatrail-message-mutation-applied` wired
      // for ACCEPT-01 operator-e2e spec (REQ-BASE-08).
      const chipsToShow = msg.subjects.slice(0, 5);
      const overflow = msg.subjects.length - chipsToShow.length;
      const deepLinkTarget = msg.subjects[0] ?? null;

      // F29 — creators get a task-first headline (no triple counts, no IRI
      // chips, no raw tool id, no ontology deep-link). The tool→verb map only
      // covers the success tool union (deletes never reach mutation-applied —
      // they report as add_block on mutation-failed, so the overload risk that
      // forbids verb-mapping mutation-failed does NOT apply here). The first
      // subject's lastSegment is a real fact (the changed node), summarized in
      // plain words; with no subjects we show the bare verb.
      if (!developer) {
        const target = msg.subjects[0] ? lastSegment(msg.subjects[0]) : null;
        return (
          <AiBubble
            icon={CheckCircle2}
            iconColor={OK}
            testId="chatrail-message-mutation-applied"
          >
            {chatCopy.mutation.appliedVerb[msg.tool]}
            {target ? (
              <>
                {" "}
                — <span className="font-mono text-xs">{target}</span>
              </>
            ) : null}
          </AiBubble>
        );
      }

      return (
        <AiBubble
          icon={CheckCircle2}
          iconColor={OK}
          testId="chatrail-message-mutation-applied"
        >
          {chatCopy.mutation.appliedPrefix}{" "}
          <span
            style={{ fontFamily: FONT_MONO, fontSize: 11, color: MUTED_TEXT }}
          >
            {msg.tool}
          </span>
          {" — "}
          {chatCopy.mutation.triplesInserted(msg.insertedCount)}
          {msg.subjects.length > 0 && (
            <ul className="mt-1 flex flex-wrap gap-1">
              {chipsToShow.map((iri) => (
                <li
                  key={iri}
                  title={iri}
                  className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground"
                >
                  {lastSegment(iri)}
                </li>
              ))}
              {overflow > 0 && (
                <li className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
                  {chatCopy.mutation.moreChips(overflow)}
                </li>
              )}
            </ul>
          )}
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              onClick={() => openOntologyPanel(deepLinkTarget)}
              className="bg-transparent p-0 text-foreground underline underline-offset-2"
            >
              {chatCopy.mutation.viewProjectDetails}
            </button>
          </div>
        </AiBubble>
      );
    }
    case "mutation-failed": {
      // v2.2 Phase 029 — SHACL violation or transport error
      // Phase 032 Plan 05 — testid `chatrail-message-mutation-failed` wired
      // for ACCEPT-01 operator-e2e spec (REQ-BASE-08).
      const secondaryLine =
        msg.violationCount > 0
          ? chatCopy.mutation.shaclViolations(msg.violationCount)
          : chatCopy.mutation.transportError;

      // F29 — creators get the humanized summary ONLY: no "N SHACL violations"
      // line, no tool chip (the tool id is engine vocabulary and is overloaded
      // for deletes, so it cannot be verb-mapped safely). The summary itself can
      // echo the literal word "SHACL" (formatShaclMessage multi/zero-violation
      // cases), so strip that jargon for creators. Ask-cogo stays on both paths.
      const summaryLine = developer
        ? msg.summary
        : stripShaclJargon(msg.summary);
      return (
        <AiBubble
          icon={AlertTriangle}
          iconColor={DANGER}
          testId="chatrail-message-mutation-failed"
        >
          <strong>{summaryLine}</strong>
          {developer && (
            <div style={{ marginTop: 2, color: MUTED_TEXT }}>
              {secondaryLine}{" "}
              <span
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: MUTED_TEXT,
                }}
              >
                {chatCopy.mutation.toolWrapper(msg.tool)}
              </span>
            </div>
          )}
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              onClick={() => askCogoToFix(msg)}
              style={{
                padding: "2px 8px",
                background: PURPLE,
                color: "hsl(var(--primary-foreground))",
                border: "none",
                borderRadius: 4,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {chatCopy.mutation.tryFix}
            </button>
          </div>
        </AiBubble>
      );
    }
    case "mutation-realtime-update": {
      // v2.2 Phase 029 — single-event fallback. Grouped path → GroupedRealtimeBubble (Task 3).
      // Phase 032 Plan 05 — testid `chatrail-message-mutation-realtime-update`
      // wired for ACCEPT-01 operator-e2e spec (REQ-BASE-08). NOTE: the grouped
      // bubble (GroupedRealtimeBubble) also receives the same testid below so
      // the spec's selector binds regardless of grouping path.
      const latency = Math.round(msg.latencyMs);
      return (
        <AiBubble
          icon={Activity}
          iconColor={INK_500}
          testId="chatrail-message-mutation-realtime-update"
        >
          {chatCopy.mutation.anotherUserChangeText(msg.changeKind)}{" "}
          <span
            style={{ fontFamily: FONT_MONO, fontSize: 11, color: MUTED_TEXT }}
          >
            {chatCopy.mutation.latencyDetail(latency)}
          </span>
        </AiBubble>
      );
    }
    case "terminology-selection": {
      // Terminology selection UI plan (Task 3) — EXCEPTION-path card. Task 1's
      // buildTerminologySelectionModel only ever produces this message when
      // there is a genuine choice to make, so no extra gating happens here.
      if (terminologySelectionOutcome.status === "declared") {
        // I1: assert only what actually happened — a project write WAS
        // attempted (C2: candidate.scope is app:<slug>-shaped here, always).
        return (
          <AiBubble
            icon={CheckCircle2}
            iconColor={OK}
            testId="chatrail-message-terminology-selection"
          >
            프로젝트에{" "}
            <span style={{ fontFamily: FONT_MONO, fontSize: 11 }}>
              {terminologySelectionOutcome.scope}
            </span>{" "}
            용어 스코프를 선언했어요 — &ldquo;{terminologySelectionOutcome.term}
            &rdquo;은(는) 다음 요청부터{" "}
            <span style={{ fontFamily: FONT_MONO, fontSize: 11 }}>
              {terminologySelectionOutcome.candidateId}
            </span>
            (으)로 해석됩니다.
          </AiBubble>
        );
      }
      if (terminologySelectionOutcome.status === "phrasing-only") {
        // I1: a platform-scope pick never writes anything (I3) — this copy
        // must never claim a project write happened.
        return (
          <AiBubble
            icon={AlertCircle}
            iconColor={MUTED_TEXT}
            testId="chatrail-message-terminology-selection"
          >
            다음 요청에는{" "}
            <span style={{ fontFamily: FONT_MONO, fontSize: 11 }}>
              {terminologySelectionOutcome.candidateId}
            </span>{" "}
            표현을 사용해보세요 — 이 선택은 프로젝트에 저장되지 않았어요.
          </AiBubble>
        );
      }
      if (terminologySelectionOutcome.status === "dismissed") {
        return (
          <AiBubble
            icon={AlertCircle}
            iconColor={MUTED_TEXT}
            testId="chatrail-message-terminology-selection"
          >
            건너뛰었어요 — 정의된 coogo 용어로 다시 표현하거나, 다음에 하나를 선택해보세요.
          </AiBubble>
        );
      }
      return (
        <AiBubble
          icon={AlertCircle}
          iconColor={MUTED_TEXT}
          testId="chatrail-message-terminology-selection"
          wide
        >
          <TerminologySelectionCard
            model={msg.model}
            templateScopes={TEMPLATE_TERMINOLOGY_SCOPES}
            onSelect={(term, candidate) => {
              // C2 semantics: a pick writes candidate.scope — NEVER
              // term.scope (the diagnosis's own resolution pool, which can
              // differ from the specific candidate's own bootstrap-searched
              // scope) — and ONLY when it is app:<slug>-shaped. A
              // platform-scope candidate is a phrasing confirmation only:
              // no store write, no persist call at all (fixes I3).
              if (!TERMINOLOGY_SCOPE_PATTERN.test(candidate.scope)) {
                setTerminologySelectionOutcome({
                  status: "phrasing-only",
                  term: term.term,
                  candidateId: candidate.id,
                });
                return;
              }
              void (async () => {
                const result = await applyTerminologyScopeSelection(
                  candidate.scope,
                );
                if (result.ok || result.reason === "persist-failed") {
                  setTerminologySelectionOutcome({
                    status: "declared",
                    term: term.term,
                    candidateId: candidate.id,
                    scope: candidate.scope,
                  });
                }
                if (result.ok) return;
                useStudioStore.getState().setPendingChatNotice(
                  result.reason === "no-project"
                    ? "열려 있는 프로젝트가 없어요 — 용어를 선택하기 전에 프로젝트를 열어주세요."
                    : result.reason === "persist-failed"
                      ? "이번 세션에는 저장됐지만, 프로젝트 동기화에 실패했어요."
                      : result.reason === "scope-conflict"
                        ? "프로젝트가 이미 다른 용어 스코프를 사용하고 있어 이 선택은 건너뛰었어요."
                        : "선택을 적용할 수 없어요.",
                );
              })();
            }}
            onDismiss={() => {
              setTerminologySelectionOutcome({ status: "dismissed" });
            }}
          />
        </AiBubble>
      );
    }
    case "template-proposal": {
      // The proposal's RULE lives in `TemplateProposalExchange` — it is shown
      // on Home too, and Home has no chat thread to host it (see that file's
      // header). This case supplies only the rail's chrome, which is
      // unchanged: the same `AiBubble`, the same testId, the same icon per
      // state, `wide` on the interactive card alone.
      return (
        <TemplateProposalExchange
          proposal={{
            templateId: msg.templateId,
            displayName: msg.displayName,
            matchedTerms: msg.matchedTerms,
            prompt: msg.prompt,
          }}
          chrome={({ tone, children }) => (
            <AiBubble
              icon={tone === "accepted" ? CheckCircle2 : AlertCircle}
              iconColor={tone === "accepted" ? OK : MUTED_TEXT}
              testId="chatrail-message-template-proposal"
              wide={tone === "pending"}
            >
              {children}
            </AiBubble>
          )}
          onAccepted={({ prompt }) => {
            if (!durableOutcomeScope) return;
            settleProjectChatThreadDescriptor(
              durableOutcomeScope,
              msg.id,
              {
                route: "generation",
                status: "succeeded",
                text: strings.chat.templateProposal.accepted,
                retryable: false,
                intent: prompt,
                timestamp: Date.now(),
              },
            );
          }}
          onDeclined={(declinedPrompt) => {
            // Task 5.5 — declining a template is a fresh free-generation
            // intent, so on THIS surface it goes through the same
            // presentation-first path as every other in-chat generation
            // entry: the readiness panel renders next in this thread, and the
            // start block below runs only when the user clicks the panel's
            // generate button.
            let readinessMessageId: string | null = null;
            const generationCopy = GENERATION_PROJECT_RECOVERY_COPY[locale];
            const settleDeclinedGeneration = (
              status: Exclude<ProjectChatOutcomeStatus, "pending">,
              text: string,
              retryable: boolean,
            ) => {
              if (!durableOutcomeScope || !readinessMessageId) return;
              const outcomeId = projectChatOutcomeIdForSourceMessage(
                readinessMessageId,
              );
              const settled = settleProjectChatThreadOutcome(
                durableOutcomeScope,
                outcomeId,
                { status, text, retryable, timestamp: Date.now() },
              );
              const outcome = settled?.turns.find(
                (turn) => turn.kind === "outcome" && turn.id === outcomeId,
              );
              const studio = useStudioStore.getState();
              if (
                !outcome ||
                outcome.kind !== "outcome" ||
                studio.session?.user?.id !== durableOutcomeScope.userId ||
                studio.currentProjectId !== durableOutcomeScope.projectId
              ) {
                return;
              }
              useStudioStore.setState({
                chatMessages: studio.chatMessages.map((message) =>
                  message.id === readinessMessageId
                    ? { ...outcome, kind: "project-chat-outcome" }
                    : message,
                ),
              });
            };
            void presentGenerationReadinessInChat({
              prompt: declinedPrompt,
              replaceDurableTurnId: msg.id,
              proceed: (confirmedPrompt) => {
                const controller = new AbortController();
                useStudioStore.getState().setPendingChatNotice(
                  strings.chat.appGenerationStarting,
                );
                // Same options-object shape the composer uses for its own
                // appCreationPrompt branch (studio-composer.tsx ~line 1983).
                startAppGenerationFromPrompt(confirmedPrompt, {
                  signal: controller.signal,
                  onGenerationError: (message) =>
                    useStudioStore.getState().setPendingChatNotice(message),
                })
                  .then(async (started) => {
                    if (started.status === "clarification-required") {
                      settleDeclinedGeneration(
                        "refused",
                        generationCopy.clarification,
                        true,
                      );
                      return;
                    }
                    // Task 4 — the producer already wrote a chat notice for
                    // this status; nothing started, so there is no
                    // `.generation` to await.
                    if (started.status === "generation-readiness-blocked") {
                      settleDeclinedGeneration(
                        "refused",
                        generationCopy.blocked,
                        true,
                      );
                      return;
                    }
                    if (durableOutcomeScope && readinessMessageId) {
                      const descriptor = readStoredAppGenerationJob(
                        started.projectId,
                      );
                      bindProjectChatGeneration(
                        durableOutcomeScope,
                        projectChatOutcomeIdForSourceMessage(
                          readinessMessageId,
                        ),
                        {
                          projectId: started.projectId,
                          ...(descriptor?.requestId
                            ? { requestId: descriptor.requestId }
                            : {}),
                          ...(descriptor?.jobId
                            ? { jobId: descriptor.jobId }
                            : {}),
                        },
                        Date.now(),
                      );
                    }
                    await started.generation;
                    settleDeclinedGeneration(
                      "succeeded",
                      generationCopy.completed,
                      false,
                    );
                  })
                  .catch((err) => {
                    if (err instanceof StudioGenerationBusyError) {
                      useStudioStore.getState().setPendingChatNotice(err.userMessage);
                      settleDeclinedGeneration(
                        "refused",
                        generationCopy.busy,
                        true,
                      );
                      return;
                    }
                    console.error(
                      "[chat-messages] template-proposal decline generation failed:",
                      err,
                    );
                    // This catch used to end at the console line, so declining a
                    // template proposal and hitting ANY typed fail-closed left the
                    // user with a stale "starting…" notice and no failure at all.
                    const surfaced = surfaceStudioGenerationFailure({
                      error: err,
                      intent: declinedPrompt,
                      locale,
                    });
                    settleDeclinedGeneration(
                      "failed",
                      surfaced.notice || generationCopy.failed,
                      true,
                    );
                  });
              },
            }).then((presentation) => {
              if (!presentation) return;
              readinessMessageId = presentation.messageId;
              if (durableOutcomeScope) {
                PROJECT_CHAT_OWNED_MESSAGE_IDS.add(presentation.messageId);
              }
            });
          }}
        />
      );
    }
    case "generation-readiness": {
      // Task 5.5 — spec §3: the chat presents the readiness panel BEFORE
      // generation starts. The card owns the post-presentation lifecycle
      // (one-shot proceed, error-state retry) in its own MessageRow-local
      // state; this switch only hosts it in the thread.
      return (
        <AiBubble
          icon={ListChecks}
          iconColor={MUTED_TEXT}
          testId="chatrail-message-generation-readiness"
          wide
        >
          <GenerationReadinessChatCard msg={msg} />
        </AiBubble>
      );
    }
  }
}

function GenerationActionButton({
  icon: Icon,
  label,
  testId,
  onClick,
  disabled = false,
  variant = "secondary",
}: {
  icon: LucideIcon;
  label: string;
  testId: string;
  onClick: () => void;
  /** Lane 3 fix — Retry passes disabled={isRunning} to block in-flight aborts. */
  disabled?: boolean;
  variant?: "primary" | "secondary";
}) {
  const className =
    variant === "primary"
      ? "inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border bg-foreground px-2.5 py-1.5 text-[11px] font-semibold text-background transition-colors hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-foreground"
      : "inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border bg-background/80 px-2.5 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-background/80";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={className}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span>{label}</span>
    </button>
  );
}

interface AiBubbleProps {
  children: React.ReactNode;
  icon: LucideIcon;
  /** v2.2 Phase 029 — optional per-kind semantic icon color. undefined = inherit INK_700. */
  iconColor?: string;
  role?: CollaborationRole;
  surface?: "default" | "runtime-evidence";
  /**
   * Phase 032 Plan 05 — optional `data-testid` on the bubble's outer
   * wrapper. Wired so the Phase 029 mutation-{applied|failed|realtime-update}
   * cards expose stable selectors for the ACCEPT-01 operator-e2e spec
   * (`e2e/integration/live-operator-task-list.spec.ts`). The attribute is
   * applied to the SAME outer `<div>` that wraps the avatar + bubble, so
   * the entire card body (icon + text + deep-link button on
   * mutation-applied) is reachable from a single `getByTestId(...)`.
   *
   * Non-mutation kinds pass `undefined` → no attribute rendered → zero
   * visual or semantic change vs Phase 029.
   */
  testId?: string;
  wide?: boolean;
}

/**
 * AI / cogo message bubble — concept2 single-tone INK_100 with a muted avatar.
 *
 * Source: `docs/studio/design-concept2/src/chat.jsx:177-185` (ChatMsg cogo
 * branch) + `chat.jsx:104-115` (thinking state). Replaces the prior 3-tone
 * (success / error / default) variant scheme; status is communicated via
 * the Lucide icon at the head of the bubble, not via background color.
 *
 * Phase 029: optional `iconColor` for semantic per-kind tones (OK / DANGER /
 * neutral/status tones). undefined → Lucide inherits text color (INK_700), identical to
 * v2.1 — no visual regression on the 7 existing kinds.
 *
 * Phase 032 Plan 05: optional `testId` for the mutation-{applied|failed|
 * realtime-update} cards (PATTERNS §3.3 testid contract). Wired as an
 * attribute on the outer wrapper — purely additive; non-mutation kinds
 * are byte-identical to Phase 029 render output.
 *
 * Asymmetric corner: borderTopLeftRadius 4 (anchors the bubble to the
 * avatar). User bubble mirrors this on the bottom-right.
 */
function AiBubble({
  children,
  icon: Icon,
  iconColor,
  role = "cogo",
  surface = "default",
  testId,
  wide = false,
}: AiBubbleProps) {
  const runtimeEvidence = surface === "runtime-evidence";
  const tone = collaborationTone(runtimeEvidence ? "runtime" : role);
  return (
    <div
      className="flex gap-2 items-end"
      data-testid={testId}
      data-collaboration-role={runtimeEvidence ? "runtime" : role}
      data-theme-surface={runtimeEvidence ? "runtime-evidence" : undefined}
    >
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/50 text-muted-foreground">
        <CoogoStatusMark mode="brand" state="idle" pace="held" size="inline-compact" />
      </div>
      {/*
        Overflow contract (2026-07-30): every box between the bubble card and
        the message body must be able to shrink below its content's min-content
        width, otherwise a single `white-space: nowrap` span (`studio-ellipsis`)
        or one long unbroken token/URL floors the whole chain at the text's
        intrinsic width and the copy paints outside the chat panel, where an
        ancestor `overflow: hidden` clips it mid-sentence.

        Flex/inline-flex items default to `min-width: auto` (= min-content), so
        `min-w-0` is required on BOTH the inline-flex row and the body span —
        `max-w-[78%]` on the card alone only clamps the card, not its contents.
        Regression lock: __tests__/chat-messages.overflow.test.tsx.
      */}
      <div
        className={`min-w-0 break-words rounded-[14px] rounded-tl border border-border bg-card/90 px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-foreground shadow-sm ${
          wide ? "max-w-[calc(100%-32px)]" : "max-w-[78%]"
        }`}
      >
        <span className="inline-flex min-w-0 max-w-full items-start gap-1.5">
          <Icon
            size={14}
            aria-hidden
            className={`mt-[3px] shrink-0 ${iconColor ? "" : tone.text}`}
            style={{ color: iconColor }}
          />
          <span className="min-w-0 break-words">{children}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * v2.2 Phase 029 — grouped/expandable rendering for consecutive
 * mutation-realtime-update events within a 2-second window.
 * count===1 → non-interactive single-event copy.
 * count>1  → button with aria-expanded toggling an inline event list.
 */
function GroupedRealtimeBubble({ group }: { group: RealtimeUpdateGroup }) {
  const { locale } = useStudioLocale();
  const copy = getChatMessagesCopy(locale);
  const [expanded, setExpanded] = useState(false);
  const isGrouped = group.count > 1;
  const lastLatency = Math.round(group.latestLatencyMs);
  const panelId = `realtime-group-${group.id}-panel`;

  if (!isGrouped) {
    const ev = group.events[0];
    return (
      // Phase 032 Plan 05 — testid mirrors the single-event mutation-realtime-update
      // branch in MessageRow above (REQ-BASE-08).
      <AiBubble
        icon={Activity}
        iconColor={INK_500}
        testId="chatrail-message-mutation-realtime-update"
      >
        {copy.mutation.anotherUserChangeText(ev.changeKind)}{" "}
        <span
          style={{ fontFamily: FONT_MONO, fontSize: 11, color: MUTED_TEXT }}
        >
          {copy.mutation.latencyDetail(lastLatency)}
        </span>
      </AiBubble>
    );
  }

  const Chevron = expanded ? ChevronDown : ChevronRight;
  const eventsDisplay = [...group.events].reverse();
  return (
    // Phase 032 Plan 05 — testid mirrors the single-event mutation-realtime-update
    // branch (REQ-BASE-08). Grouped path uses the same testid so the spec's
    // selector binds regardless of which render path the runtime takes.
    <AiBubble
      icon={Activity}
      iconColor={INK_500}
      testId="chatrail-message-mutation-realtime-update"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={panelId}
        style={{
          background: "transparent",
          border: "none",
          color: "inherit",
          cursor: "pointer",
          padding: 0,
          font: "inherit",
          textAlign: "left",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Chevron size={12} aria-hidden />
        {copy.mutation.anotherUserChangesText(group.count)}{" "}
        <span
          style={{ fontFamily: FONT_MONO, fontSize: 11, color: MUTED_TEXT }}
        >
          {copy.mutation.lastLatencyDetail(lastLatency)}
        </span>
      </button>
      {expanded && (
        <ul
          id={panelId}
          className="mt-2 flex flex-col gap-1 rounded p-2"
          style={{ background: SUBTLE_BG }}
        >
          {eventsDisplay.map((ev) => (
            <li
              key={ev.id}
              style={{ fontFamily: FONT_MONO, fontSize: 11, color: INK_700 }}
            >
              {ev.changeKind} · {copy.mutation.eventSeqLabel} {ev.versionSeq ?? "—"} ·{" "}
              {Math.round(ev.latencyMs)}ms
            </li>
          ))}
        </ul>
      )}
    </AiBubble>
  );
}

/**
 * v2.2 Phase 029 — last segment of an IRI for chip display. Copied locally
 * (3 LOC) to avoid a cross-feature import shell/ide-v2 -> features/design-system.
 */
function lastSegment(iri: string): string {
  const idx = Math.max(iri.lastIndexOf("#"), iri.lastIndexOf("/"));
  return idx >= 0 ? iri.slice(idx + 1) : iri;
}
